/** Dashboard slice — the two self-invoking spreads of the reference renderVals() (script lines 4147–4236 equity P&L bars,
 *  4237–4806 PointFive radial map · gauges · mini stats · metric cards · P&L calendar · multi-account equity curves),
 *  ported verbatim. Seeded LCGs are kept bit-for-bit; copy and formats are copied exactly.
 *  Non-null assertions appear only where the reference's fixed-length loops/lookups guarantee the index. */
import type { KeyboardEvent } from 'react';
import type { DashboardRangeId, GaugePeriod } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import {
  hoverPack,
  openDrill,
  type DrillRow,
  type DrillSpec,
  type DrillTrendBar,
  type HoverPack,
  type TipRow,
  type TipSpec,
} from './overlays';
import type { PfEngineResult, PfMetricOut, PfPillarOut } from './pfEngine';
import type { PfHover } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

type Pillar = PfPillarOut;
type Metric = PfMetricOut;
const RANGE_IDS: readonly DashboardRangeId[] = ['day', 'week', 'month', 'year', 'all'];
const isRange = (k: string): k is DashboardRangeId => (RANGE_IDS as readonly string[]).includes(k);
const RANGE_BTNS: readonly (readonly [DashboardRangeId, string])[] = [
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['year', 'Year'],
  ['all', 'All Time'],
];
/** gran / tLabel copy of the reference SETS table (the series themselves live in c.data.dashboard.eqSets). */
const EQ_COPY: Record<DashboardRangeId, { gran: string; tLabel: string }> = {
  day: { gran: 'per trade', tLabel: "Today's P&L" },
  week: { gran: 'per day', tLabel: "Today's P&L" },
  month: { gran: 'per calendar day', tLabel: 'This month' },
  year: { gran: 'per month', tLabel: 'This year' },
  all: { gran: 'per quarter', tLabel: 'All time' },
};
type Band = 0 | 1 | 2 | 3 | 4;

interface Sector {
  k: string;
  sid: string;
  d: string;
  fill: string;
  stroke: string;
  sw: string;
  dash: string;
  tab: string;
  role: string;
  aria: string;
  on: () => void;
  click: () => void;
  key: (ev: KeyboardEvent) => void;
  tf: string;
}
interface Tip {
  on: boolean;
  kicker?: string;
  title?: string;
  points?: string;
  pctv?: string;
  col?: string;
  note?: string;
}
interface DetailRow {
  label: string;
  value: string;
  col: string;
}
interface Detail {
  icon: string;
  col: string;
  title: string;
  points: string;
  body: string;
  rows: DetailRow[];
}
interface CalEntry {
  label: string;
  val: number | null;
  cur: boolean;
  tipLabel: string;
}
interface McRange {
  n: number;
  sub: string;
  lbl: (i: number) => string;
  scale: number;
}

export function buildDashboard(c: Ctx) {
  const S = c.S;
  const DD = c.data.dashboard;

  /* ── Equity P&L bar chart (script 4147–4236) ───────────────────────────────────────────────────────── */
  const eq = (() => {
    const key = isRange(S.eqRange) ? S.eqRange : 'week';
    const set = { ...EQ_COPY[key], series: DD.eqSets[key] };
    const money = (v: number) => (v < 0 ? '−$' : '+$') + Math.abs(v).toLocaleString('en-US');
    const n = set.series.length;
    let last = n - 1;
    while (last > 0 && set.series[last]![1] === 0) last--;
    const maxPos = Math.max(0, ...set.series.map((s) => s[1]));
    const maxNeg = Math.max(0, ...set.series.map((s) => -s[1]));
    // halves sized from the data's real extremes, so no dead band
    const posShare = Math.min(82, Math.max(28, Math.round((maxPos / (maxPos + maxNeg || 1)) * 100)));
    const negShare = 100 - posShare;
    // thin dense axes rather than clipping them
    const step = n > 20 ? 4 : n > 14 ? 3 : n > 10 ? 2 : 1;
    // clamp radius to roughly half the slot width
    const slot = Math.max(4, Math.floor(240 / n) - 3);
    const rTop = Math.min(8, Math.round(slot / 2));
    // intensity scales with magnitude; all steps hold >=4.5:1 against both themes' surfaces
    const POS = ['var(--pos)', 'var(--pos)', 'var(--pos)', 'var(--pos)', 'var(--pos)'] as const;
    const NEG = ['var(--neg)', 'var(--neg)', 'var(--neg)', 'var(--neg)', 'var(--neg)'] as const;
    const tier = (v: number, peak: number): Band => {
      if (!peak) return 0;
      const r = Math.abs(v) / peak;
      return r >= 0.8 ? 4 : r >= 0.6 ? 3 : r >= 0.4 ? 2 : r >= 0.2 ? 1 : 0;
    };
    const HATCH = 'repeating-linear-gradient(45deg,var(--l10) 0 2px,transparent 2px 5px)';

    /* Each bar explains itself on hover and opens a breakdown on click (script 4174–4236).
     *
     * The breakdown's constituent trades are invented here rather than fetched, because at this range the dataset
     * holds a day's *total* and nothing under it. They are seeded off the bar's own index so the same bar always
     * opens the same trades, and they are summed back to the bar's real value — `splitSum` forces the last part to
     * absorb the rounding — so the panel can never contradict the figure that opened it. simulated. */
    const totalAbs = set.series.reduce((a, sr) => a + Math.abs(sr[1]), 0);
    const prng = (seed: number) => {
      let s0 = Math.abs(seed) % 233280;
      return () => {
        s0 = (s0 * 9301 + 49297) % 233280;
        return s0 / 233280;
      };
    };
    const splitSum = (total: number, k: number, seed: number): number[] => {
      const r = prng(seed);
      const w: number[] = [];
      for (let j = 0; j < k; j++) w.push(0.35 + r() * 1.3);
      const sw = w.reduce((a, b) => a + b, 0);
      const out = w.map((x) => Math.round((total * x) / sw / 10) * 10);
      for (let j = 0; j < k - 1; j++)
        if (r() < 0.3) out[j] = -Math.round((Math.abs(out[j]!) * 0.5) / 10) * 10;
      out[k - 1] = total - out.slice(0, k - 1).reduce((a, b) => a + b, 0);
      return out;
    };
    const SYMS = ['EURUSD', 'GBPJPY', 'XAUUSD', 'US30', 'NAS100', 'USDJPY', 'GBPUSD', 'BTCUSD'];
    const STRATS = ['London Open Reversal', 'Trend Continuation', 'Breakout Pullback', 'Mean Reversion'];
    const SESSN = ['Asia', 'London', 'New York'];
    const QM: Record<string, string[]> = {
      Q1: ['January', 'February', 'March'],
      Q2: ['April', 'May', 'June'],
      Q3: ['July', 'August', 'September'],
      Q4: ['October', 'November', 'December'],
    };
    const UNITN = { day: 'Trade', week: 'Trading day', month: 'Trading day', year: 'Month', all: 'Quarter' }[
      key
    ];
    const UNITL = { day: 'trade', week: 'day', month: 'day', year: 'month', all: 'quarter' }[key];
    const PERIODW = { day: 'session', week: 'week', month: 'month', year: 'year', all: 'track record' }[key];

    const bars = set.series.map(([label, v, pct], i) => {
      const cur = i === last;
      const flat = v === 0;
      const fill = flat ? 'transparent' : v > 0 ? POS[tier(v, maxPos)] : NEG[tier(v, maxNeg)];
      const upH = v > 0 && maxPos ? ((v / maxPos) * 100).toFixed(1) + '%' : '0%';
      const dnH = v < 0 && maxNeg ? ((-v / maxNeg) * 100).toFixed(1) + '%' : '0%';
      const show = cur || i % step === 0;
      const share = totalAbs ? Math.round((Math.abs(v) / totalAbs) * 100) : 0;
      const seed = (i + 1) * 131 + key.charCodeAt(0) * 17 + 7919;
      let kidParts: number[] = [];
      let kids: DrillRow[] = [];
      // A single trade has nothing under it to break down, and a flat day has nothing at all.
      if (!flat && key !== 'day') {
        const r0 = prng(seed);
        const kn = key === 'year' ? 4 : key === 'all' ? 3 : 3 + Math.floor(r0() * 4);
        kidParts = splitSum(v, kn, seed);
        const r = prng(seed + 7);
        const riskUnit = Math.max(120, Math.round(Math.abs(v) / (kn * 1.6)) || 120);
        const qm = QM[label.slice(0, 2)] ?? ['Month 1', 'Month 2', 'Month 3'];
        kids = kidParts.map((pv, j) => {
          const col = pv >= 0 ? 'var(--pos)' : 'var(--neg)';
          if (key === 'year')
            return {
              lead: 'W' + (j + 1),
              name: 'Week ' + (j + 1),
              sub: 3 + Math.floor(r() * 3) + ' trading days',
              value: money(pv),
              col,
              meta: '',
            };
          if (key === 'all')
            return {
              lead: qm[j]!.slice(0, 3),
              name: qm[j]! + ' ' + label.slice(3),
              sub: 14 + Math.floor(r() * 8) + ' trading days',
              value: money(pv),
              col,
              meta: '',
            };
          const hh = 8 + Math.floor(r() * 9),
            mm = Math.floor(r() * 60);
          return {
            lead: String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0'),
            name: SYMS[Math.floor(r() * SYMS.length)]!,
            sub: STRATS[Math.floor(r() * STRATS.length)]! + ' · ' + SESSN[Math.floor(r() * SESSN.length)]!,
            value: money(pv),
            col,
            // R multiples, so a run of trades reads as risk taken rather than as currency of different sizes
            meta: (pv >= 0 ? '+' : '−') + Math.abs(pv / riskUnit).toFixed(2) + 'R',
          };
        });
      }
      const kWin = kidParts.filter((pp) => pp > 0).length;
      const kLoss = kidParts.length - kWin;
      const gWin = kidParts.filter((pp) => pp > 0).reduce((a, b) => a + b, 0);
      const gLoss = -kidParts.filter((pp) => pp < 0).reduce((a, b) => a + b, 0);
      const kBest = kidParts.length ? Math.max(...kidParts) : 0;
      const kWorst = kidParts.length ? Math.min(...kidParts) : 0;
      const isDay = key === 'week' || key === 'month';
      const tipSpec: TipSpec = {
        head: UNITN + ' · ' + label,
        value: flat ? 'No trading' : money(v),
        valueCol: flat ? 'var(--muted)' : v > 0 ? 'var(--pos)' : 'var(--neg)',
        rows: flat
          ? []
          : ([
              {
                k: 'Return on equity',
                v: (pct >= 0 ? '+' : '−') + Math.abs(pct).toFixed(2) + '%',
                col: pct >= 0 ? 'var(--pos)' : 'var(--neg)',
              },
              { k: 'Share of ' + PERIODW, v: share + '%' },
              // No `col`, deliberately: the count is not a gain or a loss, and the drawer's own default applies.
              ...(kids.length
                ? [{ k: isDay ? 'Closed trades' : 'Sub-periods', v: String(kids.length) }]
                : []),
            ] satisfies TipRow[]),
        note: flat
          ? 'No positions closed — excluded from Day Win %'
          : 'Click for the ' + UNITL + ' breakdown',
      };
      return {
        label: show ? label : '',
        tip:
          label +
          ' · ' +
          (flat ? 'no trading' : money(v) + ' · ' + (pct >= 0 ? '+' : '−') + Math.abs(pct).toFixed(2) + '%'),
        upH: flat ? '26%' : upH,
        dnH,
        upMin: v > 0 ? '5px' : flat ? '14px' : '0px',
        dnMin: v < 0 ? '5px' : '0px',
        upBg: v > 0 ? fill : 'transparent',
        dnBg: v < 0 ? fill : 'transparent',
        upImg: flat ? HATCH : 'none',
        dnImg: 'none',
        upBorder: flat ? '1px dashed var(--l12)' : cur && v > 0 ? '2px solid var(--text)' : 'none',
        dnBorder: cur && v < 0 ? '2px solid var(--text)' : 'none',
        radTop: rTop + 'px ' + rTop + 'px 2px 2px',
        radBot: '2px 2px ' + rTop + 'px ' + rTop + 'px',
        ml: '0px',
        lblFw: cur ? '700' : '500',
        lblFg: cur ? 'var(--text)' : 'var(--faint)',
        // The bar carries `role="button"`, so it needs a name a screen reader can read out. Without this it
        // announced as an unlabelled button that did nothing — worse than not being focusable at all.
        tipText: tipSpec.head + ' · ' + tipSpec.value,
        ...hoverPack(c, tipSpec),
        click: () =>
          openDrill(c, {
            kicker: 'Net P&L · ' + UNITN,
            title: label + (key === 'day' ? ' entry' : ''),
            value: flat ? 'No trading' : money(v),
            valueCol: flat ? 'var(--muted)' : v > 0 ? 'var(--pos)' : 'var(--neg)',
            delta: flat ? '' : (pct >= 0 ? '+' : '−') + Math.abs(pct).toFixed(2) + '% on equity',
            deltaCol: v >= 0 ? 'var(--pos)' : 'var(--neg)',
            sub: share + '% of the ' + PERIODW + ' move · ' + set.gran,
            body: flat
              ? 'Nothing was closed in this ' +
                UNITL +
                '. Flat periods are excluded from Day Win % and drawn hatched on the calendar, so they neither help nor hurt the rate.'
              : key === 'day'
                ? 'This bar is a single closed entry timed at ' +
                  label +
                  '. Its percentage is measured against equity at the session open, which is why it reads small next to the day total.'
                : isDay
                  ? 'Every trade closed on ' +
                    label +
                    ', in entry order. The summary below is the same one the trading-day panel and your journal entry carry.'
                  : 'This ' +
                    UNITL +
                    ' aggregates ' +
                    kids.length +
                    ' sub-periods. Open any of them in the journal for trade-level detail.',
            stats: flat
              ? [
                  { label: 'Closed trades', value: '0' },
                  { label: 'Status', value: 'Flat', col: 'var(--muted)' },
                ]
              : key === 'day'
                ? [
                    { label: 'Net P&L', value: money(v), col: v >= 0 ? 'var(--pos)' : 'var(--neg)' },
                    {
                      label: 'On equity',
                      value: (pct >= 0 ? '+' : '−') + Math.abs(pct).toFixed(2) + '%',
                    },
                    { label: 'Share of session', value: share + '%', col: 'var(--text-mid)' },
                  ]
                : [
                    { label: isDay ? 'Closed trades' : 'Sub-periods', value: String(kids.length) },
                    { label: isDay ? 'Winners' : 'Green', value: String(kWin), col: 'var(--pos)' },
                    { label: isDay ? 'Losers' : 'Red', value: String(kLoss), col: 'var(--neg)' },
                    {
                      label: 'Win rate',
                      value: kids.length ? Math.round((kWin / kids.length) * 100) + '%' : '—',
                    },
                    { label: 'Gross win', value: '+$' + gWin.toLocaleString('en-US'), col: 'var(--pos)' },
                    { label: 'Gross loss', value: '−$' + gLoss.toLocaleString('en-US'), col: 'var(--neg)' },
                    { label: 'Best', value: money(kBest), col: 'var(--pos)' },
                    { label: 'Worst', value: money(kWorst), col: 'var(--neg)' },
                  ],
            rows: kids,
            rowsTitle: isDay ? 'Trades closed' : key === 'year' ? 'Weeks in ' + label : 'Months in ' + label,
            rowsIcon: isDay ? 'ph-list-dashes' : 'ph-calendar-blank',
            rowsMeta: kids.length ? kids.length + (isDay ? ' trades' : ' periods') : '',
            notes: flat
              ? [
                  {
                    icon: 'ph-moon',
                    col: 'var(--muted2)',
                    text: 'No exposure taken — sitting out is a decision the journal still records.',
                  },
                ]
              : [
                  {
                    icon: kBest >= 0 ? 'ph-trend-up' : 'ph-trend-down',
                    col: kBest >= 0 ? 'var(--pos)' : 'var(--neg)',
                    text:
                      (isDay ? 'The single best trade' : 'The strongest sub-period') +
                      ' contributed ' +
                      money(kBest) +
                      ' — ' +
                      (Math.abs(v) ? Math.round((Math.abs(kBest) / Math.abs(v)) * 100) : 0) +
                      '% of the ' +
                      UNITL +
                      ' result.',
                  },
                  {
                    icon: 'ph-warning-circle',
                    col: 'var(--amb-2)',
                    text: kLoss
                      ? 'Losses cost −$' +
                        gLoss.toLocaleString('en-US') +
                        ' gross; removing the worst one alone would move the ' +
                        UNITL +
                        ' to ' +
                        money(v - kWorst) +
                        '.'
                      : 'Nothing closed red here — check whether size was cut too early on the winners.',
                  },
                  {
                    icon: 'ph-clock',
                    col: 'var(--muted2)',
                    text: 'Entry clustering is what Session Adherence scores — compare this against your planned windows in Prepare.',
                  },
                ],
            ctaLabel:
              key === 'day'
                ? 'Open this trade in the journal'
                : isDay
                  ? 'Open the journal entry for ' + label
                  : 'Review ' + label + ' in the journal',
            ctaIcon: 'ph-notebook',
            ctaHint: 'Jumps to the journal, filtered to this ' + UNITL,
            go: () => c.go('journal'),
          }),
      };
    });
    // A PERIOD WITH NO TRADES IS AN ORDINARY PERIOD, not a broken one. `set.series[last]` was non-null asserted
    // with `last = n - 1`, so an empty series indexed at -1 and the card threw on `lastRow[1]` — which unwound the
    // whole application to the data error boundary, on every screen, because a trader had not traded this week.
    // That is the state of every new account, and it is what a private beta creates.
    const lastRow: readonly [string, number, number] = set.series[last] ?? ['—', 0, 0];
    // The card's P&L is the movement of the range on screen, measured against the equity the range opened at —
    // not a stored figure. It used to read the dataset's header triple, which meant switching to Day still showed
    // the year's +$42,692 · +8.54%: three numbers that were only ever true for one of the five buttons.
    const NOW = DD.equity;
    const periodPnl = set.series.reduce((a, sr) => a + sr[1], 0);
    const cash = (v: number) =>
      '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const openEq = NOW - periodPnl;
    const periodPct = openEq ? (periodPnl / openEq) * 100 : 0;
    return {
      eqTotal: cash(NOW),
      eqPnl: (periodPnl >= 0 ? '+' : '−') + cash(periodPnl),
      eqPnlPct: (periodPct >= 0 ? '+' : '−') + Math.abs(periodPct).toFixed(2) + '%',
      eqPnlPctCol: periodPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      eqGranularity: set.gran,
      eqTodayLabel: set.tLabel,
      eqToday: money(lastRow[1]),
      eqTodayCol: lastRow[1] >= 0 ? 'var(--pos)' : 'var(--neg)',
      eqTodaySub: lastRow[0] + ' · ' + (lastRow[2] >= 0 ? '+' : '−') + Math.abs(lastRow[2]).toFixed(2) + '%',
      eqBars: bars,
      eqPosShare: posShare + '%',
      eqNegShare: negShare + '%',
      eqRanges: RANGE_BTNS.map(([id, lbl]) => ({
        label: lbl,
        go: () => c.set({ eqRange: id }),
        bg: key === id ? 'var(--rail)' : 'transparent',
        fg: key === id ? 'var(--rail-ink)' : 'var(--muted)',
        fw: key === id ? '600' : '500',
      })),
    };
  })();

  /* ── PointFive radial map · gauges · mini stats · metric cards · calendar · equity curves (script 4237–4806) ── */
  const rest = (() => {
    const E: PfEngineResult = c.pfEngine();
    const CC = 150,
      IN0 = 64,
      IN1 = 96,
      OUT0 = 100,
      OUT1 = 126,
      MOD0 = 132,
      MOD1 = 141;
    const TAU = Math.PI * 2,
      A0 = -Math.PI / 2;
    const pt = (a: number, r: number) =>
      (CC + Math.cos(a) * r).toFixed(2) + ',' + (CC + Math.sin(a) * r).toFixed(2);
    const arc = (a0: number, a1: number, r0: number, r1: number) => {
      const m = (a0 + a1) / 2;
      return (
        'M' +
        pt(a0, r1) +
        'A' +
        r1 +
        ',' +
        r1 +
        ' 0 0 1 ' +
        pt(m, r1) +
        'A' +
        r1 +
        ',' +
        r1 +
        ' 0 0 1 ' +
        pt(a1, r1) +
        'L' +
        pt(a1, r0) +
        'A' +
        r0 +
        ',' +
        r0 +
        ' 0 0 0 ' +
        pt(m, r0) +
        'A' +
        r0 +
        ',' +
        r0 +
        ' 0 0 0 ' +
        pt(a0, r0) +
        'Z'
      );
    };
    // data-lint-allow: score grade thresholds and their tones (presentation map from the reference)
    const GRADE = [
      [0.8, 'Strong', 'var(--pos)', 'var(--pos)', 82],
      [0.6, 'Moderate', 'var(--sky-fill)', 'var(--sky)', 64],
      [0.4, 'Developing', 'var(--amb)', 'var(--amb-2)', 48],
      [0, 'Weak', 'var(--neg-fill)', 'var(--neg)', 34],
    ] as const;
    type Grade = (typeof GRADE)[number];
    const wash = (g: Grade, step?: number) =>
      'color-mix(in srgb, ' + g[2] + ' ' + Math.max(24, g[4] - (step || 0) * 7) + '%, transparent)';
    const grade = (f: number): Grade => GRADE.find((g) => f >= g[0]) || GRADE[3];
    const pill: Pillar | undefined =
      S.pfPillar === null ? undefined : E.pillars.find((p) => p.id === S.pfPillar);
    const drilled = !!pill || S.pfPillar === 'mod';
    const shr = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0) + '%';
    const setHover = (h: PfHover | null) => c.set({ pfHover: h });
    const keyFor = (fn: () => void) => (ev: KeyboardEvent) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        fn();
      } else if (ev.key === 'Escape') c.set({ pfPillar: null, pfMetric: null, pfHover: null });
    };
    const noop = () => {};
    const sectors: Sector[] = [];
    const layout: { p: Pillar; a0: number; a1: number; step?: number }[] = []; // {pillar, a0, a1}
    if (pill) {
      layout.push({ p: pill, a0: A0, a1: A0 + TAU - 0.001 });
    } else {
      let acc = A0;
      E.pillars.forEach((p) => {
        const span = (p.max / E.max_score) * TAU - 0.085;
        layout.push({ p, a0: acc, a1: acc + span, step: layout.length });
        acc += span + 0.085;
      });
    }
    layout.forEach(({ p, a0, a1, step }) => {
      const f = p.points / p.max,
        g = grade(f);
      const rFill = IN0 + f * (IN1 - IN0);
      const sel = !!pill && pill.id === p.id;
      const click = () => c.set({ pfPillar: sel && !S.pfMetric ? null : p.id, pfMetric: null });
      sectors.push({
        k: 'pillar',
        sid: p.id,
        d: arc(a0, a1, IN0, IN1),
        fill: 'var(--l6)',
        stroke: 'none',
        sw: '0',
        dash: 'none',
        tab: '-1',
        role: 'presentation',
        aria: '',
        on: noop,
        click: noop,
        key: noop,
        tf: 'none',
      });
      sectors.push({
        k: 'pillar',
        sid: p.id,
        d: arc(a0, a1, IN0, rFill),
        fill: wash(g, step || 0),
        stroke: sel ? 'var(--text)' : 'none',
        sw: sel ? '1.6' : '0',
        dash: 'none',
        tab: '0',
        role: 'button',
        aria:
          p.name +
          ': ' +
          p.points +
          ' of ' +
          p.max +
          ' points, ' +
          shr(p.points, p.max) +
          ' complete, ' +
          g[1] +
          '. Activate to expand its metrics.',
        on: () => setHover({ k: 'pillar', id: p.id, a: (a0 + a1) / 2, r: (IN0 + IN1) / 2 }),
        click,
        key: keyFor(click),
        tf: 'none',
      });
      const mmax = p.metrics.reduce((x, m) => x + m.max, 0);
      let ma = a0;
      p.metrics.forEach((m) => {
        const span = (a1 - a0) * (m.max / mmax);
        const mf = m.points / m.max,
          mg = grade(mf);
        const rf = OUT0 + mf * (OUT1 - OUT0);
        const mSel = S.pfMetric === p.id + ':' + m.name;
        const mClick = () => c.set({ pfPillar: p.id, pfMetric: mSel ? null : p.id + ':' + m.name });
        sectors.push({
          k: 'metric',
          sid: p.id + ':' + m.name,
          d: arc(ma + 0.008, ma + span - 0.008, OUT0, OUT1),
          fill: 'var(--l5)',
          stroke: 'none',
          sw: '0',
          dash: 'none',
          tab: '-1',
          role: 'presentation',
          aria: '',
          on: noop,
          click: noop,
          key: noop,
          tf: 'none',
        });
        sectors.push({
          k: 'metric',
          sid: p.id + ':' + m.name,
          d: arc(ma + 0.008, ma + span - 0.008, OUT0, m.data_limited ? OUT1 : rf),
          fill: m.data_limited ? 'url(#pfhatch)' : wash(mg, (step || 0) * 0.5),
          stroke: mSel ? 'var(--text)' : 'none',
          sw: mSel ? '1.6' : '0',
          dash: 'none',
          tab: '0',
          role: 'button',
          aria:
            m.name +
            ', ' +
            p.name +
            ': ' +
            (m.data_limited ? 'data limited, scored at floor, ' : '') +
            m.points +
            ' of ' +
            m.max +
            ' points. Activate for detail.',
          on: () =>
            setHover({ k: 'metric', id: p.id + ':' + m.name, a: ma + span / 2, r: (OUT0 + OUT1) / 2 }),
          click: mClick,
          key: keyFor(mClick),
          tf: 'none',
        });
        ma += span;
      });
    });
    const modSpan = (Math.abs(E.modifier) / 50) * (Math.PI / 2);
    const modPos = E.modifier >= 0;
    const modA0 = modPos ? A0 : A0 - modSpan,
      modA1 = modPos ? A0 + modSpan : A0;
    const modClick = () => c.set({ pfPillar: S.pfPillar === 'mod' ? null : 'mod', pfMetric: null });
    sectors.push({
      k: 'mod',
      sid: 'mod',
      d: arc(A0 - Math.PI / 2.4, A0 + Math.PI / 2.4, MOD0, MOD1),
      fill: 'var(--l6)',
      stroke: 'none',
      sw: '0',
      dash: 'none',
      tab: '-1',
      role: 'presentation',
      aria: '',
      on: noop,
      click: noop,
      key: noop,
      tf: 'none',
    });
    sectors.push({
      k: 'mod',
      sid: 'mod',
      d: arc(modA0, modA1, MOD0, MOD1),
      fill: modPos
        ? 'color-mix(in srgb, var(--pos) 82%, transparent)'
        : 'color-mix(in srgb, var(--neg-fill) 82%, transparent)',
      stroke: S.pfPillar === 'mod' ? 'var(--text)' : 'none',
      sw: S.pfPillar === 'mod' ? '1.6' : '0',
      dash: 'none',
      tab: '0',
      role: 'button',
      aria:
        'Cross-pillar modifier ' +
        (modPos ? 'plus ' : 'minus ') +
        Math.abs(E.modifier) +
        ' of 50. Activate for synergies and penalties.',
      on: () => setHover({ k: 'mod', id: 'mod', a: (modA0 + modA1) / 2, r: (MOD0 + MOD1) / 2 }),
      click: modClick,
      key: keyFor(modClick),
      tf: 'none',
    });
    const H = S.pfHover;
    const zoom = (k: string, id: string) =>
      H && H.k === k && H.id === id ? 'translate(150,150) scale(1.055) translate(-150,-150)' : 'none';
    sectors.forEach((s) => {
      s.tf = zoom(s.k, s.sid);
    });
    const findMetric = (id: string): { p: Pillar; m: Metric | undefined } | null => {
      const [pid, name] = String(id).split(':');
      const pp = E.pillars.find((x) => x.id === pid);
      return pp ? { p: pp, m: pp.metrics.find((x) => x.name === name) } : null;
    };
    let tip: Tip = { on: false };
    if (H) {
      if (H.k === 'pillar') {
        const p = E.pillars.find((x) => x.id === H.id);
        if (p) {
          const sorted = p.metrics
            .filter((m) => !m.data_limited)
            .slice()
            .sort((a, b) => b.points / b.max - a.points / a.max);
          tip = {
            on: true,
            kicker: 'Pillar',
            title: p.name,
            points: p.points + ' / ' + p.max,
            pctv: shr(p.points, p.max),
            col: grade(p.points / p.max)[3],
            note: sorted.length
              ? 'Strongest ' +
                sorted[0]!.name +
                ' · weakest ' +
                sorted[sorted.length - 1]!.name +
                '. Click to expand its metrics.'
              : // Every metric in the pillar is data-limited, which is where a new account starts. There is no
                // strongest and no weakest among things nobody measured.
                'Nothing measured yet. Click to expand its metrics.',
          };
        }
      } else if (H.k === 'metric') {
        const f = findMetric(H.id);
        if (f && f.m) {
          const fm = f.m;
          tip = {
            on: true,
            kicker: f.p.name,
            title: fm.name,
            points: fm.points + ' / ' + fm.max,
            pctv: shr(fm.points, fm.max),
            col: fm.data_limited ? 'var(--muted2)' : grade(fm.points / fm.max)[3],
            note: fm.data_limited
              ? 'Data-limited — scored at the engine floor.'
              : fm.desc + ' ' + (fm.better === 'higher' ? 'Higher is better.' : 'Lower is better.'),
          };
        }
      } else {
        tip = {
          on: true,
          kicker: 'Cross-pillar',
          title: 'Modifier',
          points: (E.modifier >= 0 ? '+' : '−') + Math.abs(E.modifier) + ' / 50',
          pctv: '',
          col: E.modifier >= 0 ? 'var(--pos)' : 'var(--neg)',
          note:
            E.synergies.length +
            ' synergy bonuses, ' +
            E.penalties.length +
            ' inconsistency penalties. Click to see them.',
        };
      }
    }
    const selMetric = S.pfMetric ? findMetric(S.pfMetric) : null;
    let detail: Detail;
    if (selMetric && selMetric.m) {
      const m = selMetric.m;
      detail = {
        icon: m.data_limited ? 'ph-warning-diamond' : 'ph-target',
        col: m.data_limited ? 'var(--amb-2)' : grade(m.points / m.max)[3],
        title: m.name,
        points: m.points + ' / ' + m.max + ' pts',
        body: m.desc + ' ' + m.explain,
        rows: [
          { label: 'Raw value', value: m.value, col: 'var(--text)' },
          { label: 'Adjusted', value: m.adjusted, col: 'var(--text)' },
          {
            label: 'Direction',
            value: m.better === 'higher' ? 'Higher better' : 'Lower better',
            col: 'var(--text-mid)',
          },
          {
            label: 'Data',
            value: m.data_limited ? 'Limited' : 'Complete',
            col: m.data_limited ? 'var(--amb-2)' : 'var(--pos)',
          },
        ],
      };
    } else if (S.pfPillar === 'mod') {
      detail = {
        icon: 'ph-arrows-merge',
        col: E.modifier >= 0 ? 'var(--pos)' : 'var(--neg)',
        title: 'Cross-pillar modifier',
        points: (E.modifier >= 0 ? '+' : '−') + Math.abs(E.modifier) + ' / ±50 pts',
        body: 'Applied by the engine after the five pillars are summed — synergy bonuses where pillars reinforce each other, inconsistency penalties where they diverge.',
        rows: E.synergies.concat(E.penalties).map((x) => ({
          label: x.label,
          value: (x.points >= 0 ? '+' : '−') + Math.abs(x.points),
          col: x.points >= 0 ? 'var(--pos)' : 'var(--neg)',
        })),
      };
    } else if (pill) {
      const sorted = pill.metrics
        .filter((m) => !m.data_limited)
        .slice()
        .sort((a, b) => b.points / b.max - a.points / a.max);
      detail = {
        icon: 'ph-crosshair',
        col: grade(pill.points / pill.max)[3],
        title: pill.name,
        points: pill.points + ' / ' + pill.max + ' pts',
        body:
          'Outer ring shows this pillar’s ' +
          pill.metrics.length +
          ' metric slots, each sized by its maximum contribution. Select a slice for its definition, values and scoring note.',
        rows: [
          { label: 'Completion', value: shr(pill.points, pill.max), col: grade(pill.points / pill.max)[3] },
          // `sorted` holds the metrics that were actually MEASURED. On a new account every metric is data-limited,
          // so it is empty — and a pillar with nothing measured has no strongest and no weakest. Reading `[0]`
          // through that assertion threw, which unwound the whole app for a trader whose only fault was not having
          // traded yet. "—" is the honest answer, and the Data-limited row below already says why.
          ...(sorted.length
            ? [
                { label: 'Strongest', value: sorted[0]!.name, col: 'var(--pos)' },
                { label: 'Weakest', value: sorted[sorted.length - 1]!.name, col: 'var(--neg)' },
              ]
            : [{ label: 'Measured', value: 'nothing yet', col: 'var(--muted)' }]),
          {
            label: 'Data-limited',
            value: String(pill.metrics.filter((m) => m.data_limited).length),
            col: 'var(--amb-2)',
          },
        ],
      };
    } else {
      detail = {
        icon: 'ph-medal',
        col: 'var(--acc-deep)',
        title: 'Five-pillar overview',
        points: E.pillar_total + ' / ' + E.max_score + ' pts',
        body: 'Inner ring: each pillar sized by its maximum contribution, filled by points earned. Outer ring: all 33 metric slots. Hatched slices are data-limited. Click any sector to drill in.',
        rows: E.pillars.map((p) => ({
          label: p.name,
          value: p.points + '/' + p.max,
          col: grade(p.points / p.max)[3],
        })),
      };
    }
    const confPct = Math.round(E.confidence * 100) + '%';
    const pfView = {
      pfTip:
        'PointFive Score v' +
        E.score_version +
        ' — five pillars, 33 metric slots and a ±50 cross-pillar modifier on a 0–1,000 scale. Values come from the scoring engine (Bayesian shrinkage, 18-month time decay, regime processing); the interface never recalculates them.',
      pfScope: E.scope,
      pfScopeCol: E.scope_color,
      pfScopeTip: E.scope_is_global
        ? 'Eligible trades from every account are combined and scored as one dataset — never an average of account scores.'
        : 'Scored on this account’s normalised trade history only. Follows the account selected on the equity curve.',
      pfAsOf: E.as_of + ' · ' + E.resolution,
      pfDrilled: drilled,
      pfBack: () => c.set({ pfPillar: null, pfMetric: null, pfHover: null }),
      pfLeave: () => setHover(null),
      pfAria:
        'PointFive Score ' +
        E.final_score +
        ' of 1000, tier ' +
        E.displayed_tier +
        ', confidence ' +
        confPct +
        ', ' +
        E.percentile_label +
        ' percentile. Radial map of five pillars and 33 metrics.',
      pfSectors: sectors,
      pfCenterKicker: pill ? 'Pillar' : S.pfPillar === 'mod' ? 'Modifier' : 'PointFive Score',
      pfCenterValue: pill
        ? String(pill.points)
        : S.pfPillar === 'mod'
          ? E.modifier >= 0
            ? '+' + E.modifier
            : String(E.modifier)
          : E.final_score.toLocaleString('en-US'),
      pfMaxLabel: pill ? pill.max + ' pts' : S.pfPillar === 'mod' ? '±50' : '1,000',
      pfCenterTier: pill ? pill.name : S.pfPillar === 'mod' ? 'Cross-pillar' : E.displayed_tier,
      pfTierCol: pill
        ? grade(pill.points / pill.max)[3]
        : S.pfPillar === 'mod'
          ? E.modifier >= 0
            ? 'var(--pos)'
            : 'var(--neg)'
          : grade(E.final_score / 1000)[3],
      pfCenterSub: pill
        ? shr(pill.points, pill.max) + ' complete'
        : S.pfPillar === 'mod'
          ? E.synergies.length + ' synergies · ' + E.penalties.length + ' penalties'
          : confPct + ' confidence · ' + E.percentile_label + ' pct',
      pfStatKicker: pill ? 'Pillar' : S.pfPillar === 'mod' ? 'Layer' : 'Displayed tier',
      pfConf: confPct,
      pfConfCol: E.confidence >= 0.5 ? 'var(--pos)' : 'var(--amb-2)',
      pfConfNote:
        E.trades.toLocaleString('en-US') +
        ' trades · ' +
        E.track_record_months +
        ' mo · ' +
        E.data_limited_count +
        ' data-limited · ' +
        E.input_missing_count +
        ' not sent',
      pfPercentile: E.percentile_label,
      pfHoverOn: tip.on,
      pfTipLeft: H ? (((CC + Math.cos(H.a) * H.r) / 300) * 100).toFixed(2) + '%' : '50%',
      pfTipTop: H ? (((CC + Math.sin(H.a) * H.r) / 300) * 100).toFixed(2) + '%' : '50%',
      pfTipTf: H && (CC + Math.sin(H.a) * H.r) / 300 < 0.46 ? 'translate(-50%,16%)' : 'translate(-50%,-114%)',
      pfTipKicker: tip.kicker || '',
      pfTipTitle: tip.title || '',
      pfTipPoints: tip.points || '',
      pfTipPct: tip.pctv || '',
      pfTipPctCol: tip.col || 'var(--muted)',
      pfTipNote: tip.note || '',
      pfLegend: GRADE.map((g) => ({
        name: g[1] + (g[0] ? ' ≥' + Math.round(g[0] * 100) + '%' : ' <40%'),
        col: wash(g),
        img: 'none',
        bd: g[2] as string,
        bdStyle: 'solid',
        tip: g[1] + ' — points earned against the slot maximum',
      })).concat([
        {
          name: 'Data-limited',
          col: 'transparent',
          img: 'repeating-linear-gradient(45deg,var(--muted2) 0 2px,transparent 2px 5px)',
          bd: 'var(--muted2)',
          bdStyle: 'dashed',
          tip:
            'Required inputs absent — the engine scored the slot at its data-limited floor. ' +
            E.data_limited_count +
            ' slots affected.',
        },
      ]),
      pfSnapRes: E.resolution,
      pfSnaps: E.snapshot_labels.map((lbl, i) => {
        const on = i === E.snapshot_index;
        const share = (i + 1) / E.snapshot_labels.length;
        const approx = Math.round(E.final_score * (0.82 + 0.18 * share));
        return {
          label: lbl,
          go: () => c.set({ pfSnap: i, pfHover: null, pfMetric: null }),
          h: Math.max(8, Math.round((approx / 1000) * 100)) + '%',
          col: on ? grade(approx / 1000)[2] : 'var(--l10)',
          op: on ? '1' : '0.75',
          fg: on ? 'var(--text)' : 'var(--faint)',
          fw: on ? '700' : '500',
          tip:
            'As of ' + lbl + ' · scored on all eligible history to that point' + (on ? ' · showing now' : ''),
          ...hoverPack(c, {
            head: 'Score evolution · ' + lbl,
            value: approx.toLocaleString('en-US') + ' / 1,000',
            valueCol: grade(approx / 1000)[3],
            rows: [
              { k: 'Band', v: grade(approx / 1000)[1], col: grade(approx / 1000)[3] },
              { k: 'Basis', v: 'all history to ' + lbl },
            ],
            note: on ? 'Showing this snapshot now' : 'Click to score as of ' + lbl,
          }),
        };
      }),
      pfDetailIcon: detail.icon,
      pfDetailCol: detail.col,
      pfDetailTitle: detail.title,
      pfDetailPoints: detail.points,
      pfDetailBody: detail.body,
      pfDetailRows: detail.rows,
      pfTierReduced: E.tier_reduced_by > 0,
      pfTierNote:
        'Raw tier ' +
        E.raw_tier +
        ' · displayed tier ' +
        E.displayed_tier +
        '. Confidence is ' +
        confPct +
        ' (' +
        E.trades.toLocaleString('en-US') +
        ' trades, ' +
        E.track_record_months +
        ' months, ' +
        E.data_limited_count +
        ' data-limited, ' +
        E.input_missing_count +
        ' not sent by the broker), so the engine reduced the displayed tier by ' +
        E.tier_reduced_by +
        '.',
      pfFacts: [
        {
          label: 'Trades',
          value: E.trades.toLocaleString('en-US'),
          col: 'var(--text)',
          tip: 'Eligible closed trades in this scope',
        },
        {
          label: 'Track record',
          value: E.track_record_months + ' mo',
          col: 'var(--text)',
          tip: 'Verified continuous history',
        },
        {
          label: 'Date range',
          value: E.date_range,
          col: 'var(--text-mid)',
          tip: 'First to latest eligible trade',
        },
        {
          label: 'Win rate',
          value: E.win_rate,
          col: 'var(--text)',
          tip: 'Winning closed trades ÷ closed trades',
        },
        { label: 'Net P&L', value: E.pnl, col: 'var(--pos)', tip: 'Realised P&L in scope' },
        {
          label: 'Regimes',
          value: E.regimes.bull + '/' + E.regimes.bear + '/' + E.regimes.sideways,
          col: 'var(--text-mid)',
          tip: 'Bull / bear / sideways coverage, % of history',
        },
        {
          label: 'Raw tier',
          value: E.raw_tier,
          col: 'var(--text-mid)',
          tip: 'Tier before the confidence adjustment',
        },
        {
          label: 'Displayed tier',
          value: E.displayed_tier,
          col: grade(E.final_score / 1000)[3],
          tip: 'Confidence-adjusted tier returned by the engine',
        },
        { label: 'Percentile', value: E.percentile_label, col: 'var(--text)', tip: 'Population percentile' },
        {
          label: 'Confidence',
          value: confPct,
          col: E.confidence >= 0.5 ? 'var(--pos)' : 'var(--amb-2)',
          tip: 'From trade count, track-record length and the two absence counts below',
        },
        {
          // v1.4 §4.5 keeps the two absences apart, and so does this row: the first number is history the trader
          // can still build, the second is a field their broker never sent and no amount of trading will supply.
          label: 'Data-limited',
          value: E.data_limited_count + ' + ' + E.input_missing_count + ' of 33',
          col: E.data_limited_count || E.input_missing_count ? 'var(--amb-2)' : 'var(--pos)',
          tip: 'Metric slots at the data-limited floor, then those your broker did not send',
        },
        {
          label: 'Modifier',
          value: (E.modifier >= 0 ? '+' : '−') + Math.abs(E.modifier),
          col: E.modifier >= 0 ? 'var(--pos)' : 'var(--neg)',
          tip: 'Cross-pillar modifier, range ±50',
        },
        // Every fact already carries its own explanation in `tip`; the hover pack just promotes that from a
        // browser title to the same tooltip the rest of the dashboard uses. Nothing new is asserted here.
      ].map((f) => ({
        ...f,
        ...hoverPack(c, { head: f.label, value: f.value, valueCol: f.col, rows: [], note: f.tip }),
      })),
      pfVersions: 'score_version ' + E.score_version + ' · calibration_version ' + E.calibration_version,
    };

    /* gauge cards · mini stats · metric cards */
    const G = DD.gauges;
    const gk = isRange(S.gRange) ? S.gRange : 'month',
      D: GaugePeriod = G[gk];
    const tradePct = D.closed ? (D.wins / D.closed) * 100 : 0;
    const dayPct = D.daysTraded ? (D.daysProfit / D.daysTraded) * 100 : 0;
    const ticks = (pct: number) => {
      const N = 40,
        lit = (pct / 100) * N,
        out: { x1: string; y1: string; x2: string; y2: string; col: string; w: string; op: string }[] = [];
      for (let i = 0; i < N; i++) {
        const a = Math.PI * (1 - (i + 0.5) / N);
        const on = i < lit,
          cur = on && i + 1 >= lit;
        const ro = cur ? 100 : on ? 96 : 92,
          ri = cur ? 66 : on ? 72 : 76;
        out.push({
          x1: (100 + Math.cos(a) * ri).toFixed(1),
          y1: (104 - Math.sin(a) * ri).toFixed(1),
          x2: (100 + Math.cos(a) * ro).toFixed(1),
          y2: (104 - Math.sin(a) * ro).toFixed(1),
          col: on ? (cur ? 'var(--acc-deep)' : 'var(--acc-fill)') : 'var(--l10)',
          w: cur ? '3.4' : on ? '2.6' : '2.2',
          op: on ? (cur ? '1' : (0.55 + 0.45 * (i / Math.max(1, lit))).toFixed(2)) : '1',
        });
      }
      return out;
    };
    const num = (v: number) => v.toLocaleString('en-US');
    const roiFill = Math.min(100, (D.roi / D.scale) * 100);
    // stacked metrics beside the score dial, on the same timeframe data
    let ms = (gk.charCodeAt(0) * 4177 + 7919) % 233280;
    const mr = () => {
      ms = (ms * 9301 + 49297) % 233280;
      return ms / 233280;
    };
    // gross figures are solved from the trade counts and net P&L, so PF and R stay in a realistic band
    const losers = Math.max(1, D.closed - D.wins);
    const payoff = 1.45 + (gk.charCodeAt(0) % 5) * 0.09; // target avg win ÷ avg loss
    const avgLoss = Math.max(40, Math.round(D.pnl / Math.max(0.6, D.wins * payoff - losers)));
    const avgWin = Math.round(avgLoss * payoff);
    const grossWin = avgWin * D.wins;
    const grossLoss = avgLoss * losers;
    const pf = grossLoss ? grossWin / grossLoss : Infinity;
    const ratio = avgLoss ? avgWin / avgLoss : Infinity;
    // the reference also seeds a 12-segment pnlSeries (`mr()`) and a `bar()` helper here, but never returns them — dead code, omitted
    void mr;
    const SESS = DD.sessions;
    const sessPnl = SESS.map(([name, share, wr]) => ({ name, pnl: Math.round(D.pnl * share), wr }));
    const sessPeak = Math.max(1, ...sessPnl.map((s) => Math.abs(s.pnl)));
    // A trader with no sessions has no best or worst one. `sessPnl` is empty until the dashboard aggregate has
    // sessions in it, and reading `[0]!` through that emptiness threw — taking every screen down, not just this
    // card. The placeholder names the absence rather than nominating a winner from a field of none.
    const NO_SESSION = { name: '—', pnl: 0, wr: 0 };
    const bestSess = sessPnl.slice().sort((a, b) => b.pnl - a.pnl)[0] ?? NO_SESSION;
    const worstSess = sessPnl.slice().sort((a, b) => a.pnl - b.pnl)[0] ?? NO_SESSION;
    const HOUR_W = DD.hourWeights;
    const HOUR_L = DD.hourLabels;
    // wider buckets when the sample is thin, so buckets never collapse into a run of ties
    const blockSize = D.closed < 24 ? 4 : D.closed < 60 ? 2 : 1;
    const buckets: { label: string; hour: string; w: number }[] = [];
    for (let i = 0; i < HOUR_L.length; i += blockSize) {
      const grp = HOUR_W.slice(i, i + blockSize);
      buckets.push({
        label:
          blockSize === 1
            ? HOUR_L[i]!
            : HOUR_L[i]! + '–' + HOUR_L[Math.min(HOUR_L.length - 1, i + blockSize - 1)]!,
        hour: HOUR_L[i]!,
        w: grp.reduce((a, b) => a + b, 0),
      });
    }
    // largest-remainder split so the buckets sum to exactly D.closed
    const wSum = buckets.reduce((a, b) => a + b.w, 0);
    const exact = buckets.map((b) => (D.closed * b.w) / wSum);
    const hourTrades = exact.map(Math.floor);
    let short = D.closed - hourTrades.reduce((a, b) => a + b, 0);
    exact
      .map((v, i): [number, number] => [i, v - Math.floor(v)])
      .sort((a, b) => b[1] - a[1])
      .forEach(([i]) => {
        if (short > 0) {
          hourTrades[i]!++;
          short--;
        }
      });
    const hourTotal = D.closed;
    // NO TRADES MEANS NO PEAK HOUR. `Math.max(...[])` is −Infinity, `indexOf` then returns −1, and `buckets[-1]!`
    // is undefined — so reading `.label` threw and took the whole application down for an account whose only
    // property was not having traded yet. A trader with no closed trades has no busiest hour, and saying so is the
    // answer; inventing one from an empty distribution would be worse than the crash.
    const hourPeak = hourTrades.length ? Math.max(...hourTrades) : 0;
    const tied = hourTrades.filter((v) => v === hourPeak).length > 1;
    const peakIdx = hourTrades.indexOf(hourPeak);
    const peakHour = buckets[peakIdx]?.label ?? '—';
    const plural = (v: number) => v + (v === 1 ? ' trade' : ' trades');
    let cum = 0,
      medianHour = buckets[0]?.label ?? '—';
    for (let i = 0; i < hourTrades.length; i++) {
      cum += hourTrades[i]!;
      if (cum >= hourTotal / 2) {
        medianHour = buckets[i]?.label ?? medianHour;
        break;
      }
    }
    const expectancy = Math.round(D.pnl / Math.max(1, D.closed));
    // caps derive from the value so a meter can never max out against a caption that disagrees
    const nice = (v: number, floor: number, stepList: number[]) => {
      const t0 = Math.max(floor, v * 1.15);
      return stepList.find((s) => s >= t0) || Math.ceil(t0);
    };
    // data-lint-allow: nice-scale steps — a meter’s stated scale is derived from the value using these (§9.4)
    const pfCap = nice(isFinite(pf) ? pf : 3, 2, [2, 3, 4, 5, 6, 8, 10, 15, 20]);
    // data-lint-allow: nice-scale steps for the R ratio meter (§9.4)
    const rCap = nice(isFinite(ratio) ? ratio : 2.5, 1.5, [1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]);
    // data-lint-allow: nice-scale steps for the expectancy meter (§9.4)
    const eCap = nice(Math.abs(expectancy), 100, [100, 200, 500, 1000, 2000, 5000, 10000]);
    const miniCards = [
      {
        label: 'Win rate',
        value: tradePct.toFixed(1) + '%',
        col: 'var(--text)',
        chg: num(D.wins) + '/' + num(D.closed),
        chgCol: 'var(--muted2)',
        fill: Math.min(100, tradePct).toFixed(1) + '%',
        barCol: tradePct >= 50 ? 'var(--pnl-p3)' : 'var(--pnl-n3)',
        sub: 'closed trades ' + D.label,
        tip: 'Winning closed trades ÷ total closed trades × 100 ' + D.label + '. Open positions excluded.',
      },
      {
        label: 'Profit factor',
        value: isFinite(pf) ? pf.toFixed(2) : '∞',
        col: 'var(--text)',
        chg: pf >= 1.5 ? 'healthy' : pf >= 1 ? 'thin' : 'negative',
        chgCol: pf >= 1.5 ? 'var(--pos)' : pf >= 1 ? 'var(--amb-2)' : 'var(--neg)',
        fill: Math.min(100, ((isFinite(pf) ? pf : pfCap) / pfCap) * 100).toFixed(1) + '%',
        barCol: pf >= 1.5 ? 'var(--pnl-p3)' : pf >= 1 ? 'var(--amb)' : 'var(--pnl-n3)',
        sub: 'scale 0 – ' + pfCap.toFixed(2) + ' · 1.00 breakeven',
        tip:
          'Gross profit ÷ gross loss ' +
          D.label +
          ' — +$' +
          num(grossWin) +
          ' against −$' +
          num(grossLoss) +
          '. Above 1.00 is profitable.',
      },
      {
        label: 'Expectancy',
        value: (expectancy >= 0 ? '+$' : '−$') + num(Math.abs(expectancy)),
        col: expectancy >= 0 ? 'var(--pos)' : 'var(--neg)',
        chg: 'per trade',
        chgCol: 'var(--muted2)',
        fill: Math.min(100, (Math.abs(expectancy) / eCap) * 100).toFixed(1) + '%',
        barCol: expectancy >= 0 ? 'var(--pnl-p3)' : 'var(--pnl-n3)',
        sub: 'scale 0 – $' + num(eCap) + ' · ' + num(D.closed) + ' trades',
        tip:
          'Net P&L ÷ closed trades ' +
          D.label +
          ' — the average expected outcome of taking one more trade at your current edge.',
      },
      {
        label: 'Avg win / loss',
        value: '$' + num(avgWin) + ' / $' + num(avgLoss),
        col: 'var(--text)',
        chg: (isFinite(ratio) ? ratio.toFixed(2) : '∞') + 'R',
        chgCol: ratio >= 1 ? 'var(--pos)' : 'var(--amb-2)',
        fill: Math.min(100, ((isFinite(ratio) ? ratio : rCap) / rCap) * 100).toFixed(1) + '%',
        barCol: ratio >= 1 ? 'var(--pnl-p3)' : 'var(--amb)',
        sub: 'scale 0 – ' + rCap.toFixed(2) + 'R · 1.00R even',
        tip:
          'Average winning trade against the average losing trade ' +
          D.label +
          '. The R multiple is avg win ÷ avg loss.',
      },
    ];
    const pfMetricCards = [
      {
        label: 'Session performance',
        value: bestSess.name,
        col: 'var(--text)',
        chg: (bestSess.pnl >= 0 ? '+$' : '−$') + num(Math.abs(bestSess.pnl)),
        chgCol: bestSess.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        tip:
          'Realised P&L by trading session ' +
          D.label +
          ', split on session open/close times in your Prepare windows. Bar height is P&L; hover a bar for its win rate.',
        bars: sessPnl.map((s) => ({
          label: s.name === 'New York' ? 'NY' : s.name,
          h: ((Math.abs(s.pnl) / sessPeak) * 100).toFixed(1) + '%',
          rad: s.pnl >= 0 ? '4px 4px 2px 2px' : '2px 2px 4px 4px',
          op: '1',
          col: s.pnl >= 0 ? 'var(--pnl-p3)' : 'var(--pnl-n3)',
          tip:
            s.name + ' · ' + (s.pnl >= 0 ? '+$' : '−$') + num(Math.abs(s.pnl)) + ' · ' + s.wr + '% win rate',
        })),
        figs: [
          {
            label: 'Best session',
            value: bestSess.name + ' · ' + bestSess.wr + '%',
            col: 'var(--pos)',
            tip:
              bestSess.name +
              ' produced ' +
              (bestSess.pnl >= 0 ? '+$' : '−$') +
              num(Math.abs(bestSess.pnl)) +
              ' ' +
              D.label,
          },
          {
            label: 'Weakest',
            value: worstSess.name + ' · ' + worstSess.wr + '%',
            col: 'var(--neg)',
            tip:
              worstSess.name +
              ' produced ' +
              (worstSess.pnl >= 0 ? '+$' : '−$') +
              num(Math.abs(worstSess.pnl)) +
              ' ' +
              D.label,
          },
        ],
      },
      {
        label: 'Trade distribution by hour',
        value: tied ? 'Level' : peakHour + ':00',
        col: 'var(--text)',
        chg: tied ? 'no single peak' : plural(hourPeak),
        chgCol: 'var(--text-mid)',
        tip:
          'All ' +
          plural(D.closed) +
          ' closed ' +
          D.label +
          ', bucketed by entry hour (broker time)' +
          (blockSize > 1 ? ' in ' + blockSize + '-hour blocks because the sample is thin' : '') +
          '. Concentration outside your planned windows is what the Session Adherence metric penalises.',
        bars: buckets.map((b, i) => ({
          label: b.label,
          h: ((hourTrades[i]! / hourPeak) * 100).toFixed(1) + '%',
          rad: '3px 3px 2px 2px',
          op: (0.45 + 0.55 * (hourTrades[i]! / hourPeak)).toFixed(2),
          col: 'var(--acc-fill)',
          tip:
            b.label +
            ':00 · ' +
            plural(hourTrades[i]!) +
            ' · ' +
            Math.round((hourTrades[i]! / Math.max(1, hourTotal)) * 100) +
            '% of volume',
        })),
        figs: [
          {
            label: tied ? 'Busiest' : 'Peak hour',
            value: tied ? 'Tied · ' + plural(hourPeak) : peakHour + ':00',
            col: 'var(--text)',
            tip: tied
              ? hourTrades.filter((v) => v === hourPeak).length +
                ' buckets tie at ' +
                plural(hourPeak) +
                ' — no single peak in this sample'
              : plural(hourPeak) + ' entered in that window',
          },
          {
            label: 'Median hour',
            value: medianHour + ':00',
            col: 'var(--text-mid)',
            tip: 'Half your volume is entered before this window',
          },
        ],
      },
    ];
    const gaugeCards = [
      {
        label: 'Trade Win %',
        min: '0%',
        max: '100%',
        pct: tradePct.toFixed(1) + '%',
        caption: 'of ' + num(D.closed) + (D.closed === 1 ? ' closed trade' : ' closed'),
        aria: 'Trade win rate ' + tradePct.toFixed(1) + ' percent',
        tip:
          'Trade Win % = winning closed trades ÷ total closed trades × 100. ' +
          num(D.wins) +
          ' of ' +
          num(D.closed) +
          ' closed trades were profitable ' +
          D.label +
          '. Open positions (' +
          D.open +
          ') are excluded until they close.',
        ticks: ticks(tradePct),
        figs: [
          {
            label: 'Winning trades',
            value: num(D.wins),
            col: 'var(--pos)',
            tip: 'Closed trades that finished in profit ' + D.label,
          },
          {
            label: 'Closed trades',
            value: num(D.closed),
            col: 'var(--text)',
            tip: 'All trades closed ' + D.label + ' — ' + D.open + ' open positions excluded',
          },
        ],
      },
      {
        label: 'Day Win %',
        min: '0%',
        max: '100%',
        pct: dayPct.toFixed(1) + '%',
        caption: 'of ' + num(D.daysTraded) + (D.daysTraded === 1 ? ' trading day' : ' trading days'),
        aria: 'Day win rate ' + dayPct.toFixed(1) + ' percent',
        tip:
          'Day Win % = profitable trading days ÷ total trading days × 100. ' +
          num(D.daysProfit) +
          ' of ' +
          num(D.daysTraded) +
          ' trading days closed green ' +
          D.label +
          '. Days with no trading activity are excluded.',
        ticks: ticks(dayPct),
        figs: [
          {
            label: D.daysProfit === 1 ? 'Profitable day' : 'Profitable days',
            value: num(D.daysProfit),
            col: 'var(--pos)',
            tip: 'Trading days that finished net positive ' + D.label,
          },
          {
            label: D.daysTraded === 1 ? 'Trading day' : 'Trading days',
            value: num(D.daysTraded),
            col: 'var(--text)',
            tip: 'Days with at least one closed trade ' + D.label + ' — flat days excluded',
          },
        ],
      },
      {
        label: 'ROI',
        min: '0%',
        max: '+' + D.scale + '%',
        pct: '+' + D.roi.toFixed(2) + '%',
        caption: 'on $500,000 deposit',
        aria: 'Return on investment ' + D.roi.toFixed(2) + ' percent',
        tip:
          'ROI = net realised P&L ÷ deposited capital × 100. +$' +
          num(D.pnl) +
          ' on $500,000 deposited ' +
          D.label +
          '. The scale runs 0% to +' +
          D.scale +
          '% for this timeframe; open positions are excluded.',
        ticks: ticks(roiFill),
        figs: [
          {
            label: 'Net P&L',
            value: '+$' + num(D.pnl),
            col: 'var(--pos)',
            tip: 'Realised profit ' + D.label + ' — open positions excluded',
          },
          {
            label: 'Deposit',
            value: '$500,000',
            col: 'var(--text)',
            tip: 'Capital deposited across all accounts',
          },
        ],
      },
    ];

    /* ── one hover tooltip and one drill-down per figure (script 4463–4700) ─────────────────────────────
     *
     * The package's rule, and the reason this block exists rather than a tooltip attribute here and there: every
     * figure on the dashboard explains itself the same way. Hover gives the number with its inputs; click opens the
     * drawer with the working. A trader should never have to learn which figures are clickable.
     *
     * `wire` returns a new card rather than mutating one, so a card's handlers and its contents are built in one
     * place and the reference's `Object.assign` mutation does not leak into a type. */
    const B = (() => {
      const d = G.all;
      // The all-time baseline every "vs all-time" delta is measured against. It re-derives gross figures with the
      // same solver the selected range uses ('all'.charCodeAt(0)) so the two are comparable rather than merely
      // adjacent — a baseline computed a different way would make every delta a comparison of two methods.
      const lo = Math.max(1, d.closed - d.wins);
      const po = 1.45 + ('all'.charCodeAt(0) % 5) * 0.09;
      const al = Math.max(40, Math.round(d.pnl / Math.max(0.6, d.wins * po - lo)));
      const aw = Math.round(al * po),
        gw = aw * d.wins,
        gl2 = al * lo;
      return {
        tradePct: (d.wins / d.closed) * 100,
        dayPct: (d.daysProfit / d.daysTraded) * 100,
        pf: gl2 ? gw / gl2 : Infinity,
        ratio: al ? aw / al : Infinity,
        expectancy: Math.round(d.pnl / d.closed),
        avgWin: aw,
        avgLoss: al,
        closed: d.closed,
      };
    })();
    const dPts = (a: number, b: number) => ({
      txt: (a - b >= 0 ? '+' : '−') + Math.abs(a - b).toFixed(1) + ' pts vs all-time',
      col: a >= b ? 'var(--pos)' : 'var(--neg)',
    });
    const dAbs = (a: number, b: number, dp: number, pre?: string) => ({
      txt: (a - b >= 0 ? '+' : '−') + (pre ?? '') + Math.abs(a - b).toFixed(dp) + ' vs all-time',
      col: a >= b ? 'var(--pos)' : 'var(--neg)',
    });
    const SUBL: string[] = {
      day: ['08', '10', '12', '14', '16'],
      week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      month: ['W1', 'W2', 'W3', 'W4'],
      year: ['Q1', 'Q2', 'Q3', 'Q4'],
      all: ['FY24', 'FY25', 'FY26'],
    }[gk];
    /** The drawer's sparkline: the metric scattered across its own sub-periods, seeded per metric so it is stable
     *  across renders. simulated — the dataset holds the period total, not a breakdown of it. */
    const trendOf = (
      center: number,
      spread: number,
      seed: number,
      fmt: (v: number) => string,
      head: string,
    ): DrillTrendBar[] => {
      let s0 = Math.abs(seed) % 233280;
      const r = () => {
        s0 = (s0 * 9301 + 49297) % 233280;
        return s0 / 233280;
      };
      const vals = SUBL.map(() => center * (1 - spread + r() * spread * 2));
      const peak = Math.max(...vals.map(Math.abs)) || 1;
      return SUBL.map((lb, i) => ({
        label: lb,
        h: ((Math.abs(vals[i]!) / peak) * 100).toFixed(1) + '%',
        col: vals[i]! >= 0 ? 'var(--pnl-p3)' : 'var(--pnl-n3)',
        op: (0.5 + (0.5 * Math.abs(vals[i]!)) / peak).toFixed(2),
        tip: {
          head: head + ' · ' + lb,
          value: fmt(vals[i]!),
          valueCol: vals[i]! >= 0 ? 'var(--pos)' : 'var(--neg)',
          rows: [],
          note: '',
        },
      }));
    };
    const jGo = () => c.go('journal');
    const anGo = () => c.go('analytics');
    const breakeven = 100 / (1 + (isFinite(ratio) ? ratio : 1));
    const sessAbs = sessPnl.reduce((a, x) => a + Math.abs(x.pnl), 0) || 1;
    const wire = <T extends object>(
      card: T,
      hover: TipSpec,
      drill: DrillSpec,
    ): T & HoverPack & { click: () => void } => ({
      ...card,
      ...hoverPack(c, hover),
      click: () => openDrill(c, drill),
    });
    // One inference site, `T`, so the row type comes off the card rather than being guessed alongside it.
    const wireFigs = <T extends { figs: readonly object[] }>(
      card: T,
      specs: TipSpec[],
    ): Omit<T, 'figs'> & { figs: (T['figs'][number] & HoverPack)[] } => ({
      ...card,
      figs: card.figs.map((f, i) => ({ ...f, ...hoverPack(c, specs[i]!) })),
    });

    const miniStats = [
      wire(
        miniCards[0]!,
        {
          head: 'Win rate · ' + D.label,
          value: tradePct.toFixed(1) + '%',
          valueCol: tradePct >= breakeven ? 'var(--pos)' : 'var(--amb-2)',
          rows: [
            { k: 'Winners', v: num(D.wins), col: 'var(--pos)' },
            { k: 'Losers', v: num(losers), col: 'var(--neg)' },
            { k: 'All-time', v: B.tradePct.toFixed(1) + '%' },
          ],
          note: 'Click for the full breakdown',
        },
        {
          kicker: 'Metric · ' + D.label,
          title: 'Win rate',
          value: tradePct.toFixed(1) + '%',
          delta: dPts(tradePct, B.tradePct).txt,
          deltaCol: dPts(tradePct, B.tradePct).col,
          sub: num(D.wins) + ' winners in ' + num(D.closed) + ' closed trades',
          body: 'Winning closed trades ÷ all closed trades. On its own it says nothing about size — a 40% hit rate with a 3R average winner beats a 70% rate that gives it back. Read it against Profit factor and Avg win / loss.',
          stats: [
            { label: 'Winners', value: num(D.wins), col: 'var(--pos)' },
            { label: 'Losers', value: num(losers), col: 'var(--neg)' },
            { label: 'Closed', value: num(D.closed) },
            { label: 'All-time', value: B.tradePct.toFixed(1) + '%', col: 'var(--text-mid)' },
            { label: 'Breakeven rate', value: breakeven.toFixed(1) + '%', col: 'var(--text-mid)' },
          ],
          trendTitle: 'Win rate by sub-period',
          trend: trendOf(tradePct, 0.2, 4177, (v) => v.toFixed(1) + '%', 'Win rate'),
          notes: [
            {
              icon: 'ph-trend-up',
              col: 'var(--pos)',
              text:
                bestSess.name +
                ' holds the strongest hit rate at ' +
                bestSess.wr +
                '% and carries the most volume.',
            },
            {
              icon: 'ph-trend-down',
              col: 'var(--neg)',
              text:
                worstSess.name +
                ' is the drag at ' +
                worstSess.wr +
                '% — trimming it lifts the blended rate without touching size.',
            },
            {
              icon: 'ph-scales',
              col: 'var(--muted2)',
              text:
                'At a ' +
                (isFinite(ratio) ? ratio.toFixed(2) : '∞') +
                'R payoff your breakeven rate is ' +
                breakeven.toFixed(1) +
                '%, so you are ' +
                (tradePct >= breakeven ? 'clear of it' : 'under it') +
                '.',
            },
          ],
          ctaLabel: 'Review these trades in the journal',
          ctaHint: 'Journal, filtered to closed trades ' + D.label,
          go: jGo,
        },
      ),
      wire(
        miniCards[1]!,
        {
          head: 'Profit factor · ' + D.label,
          value: isFinite(pf) ? pf.toFixed(2) : '∞',
          valueCol: pf >= 1.5 ? 'var(--pos)' : pf >= 1 ? 'var(--amb-2)' : 'var(--neg)',
          rows: [
            { k: 'Gross profit', v: '+$' + num(grossWin), col: 'var(--pos)' },
            { k: 'Gross loss', v: '−$' + num(grossLoss), col: 'var(--neg)' },
            { k: 'All-time', v: B.pf.toFixed(2) },
          ],
          note: 'Click for the full breakdown',
        },
        {
          kicker: 'Metric · ' + D.label,
          title: 'Profit factor',
          value: isFinite(pf) ? pf.toFixed(2) : '∞',
          delta: dAbs(pf, B.pf, 2).txt,
          deltaCol: dAbs(pf, B.pf, 2).col,
          sub: '+$' + num(grossWin) + ' gross profit against −$' + num(grossLoss) + ' gross loss',
          body: 'Gross profit ÷ gross loss. 1.00 is breakeven; under it the account bleeds. Past roughly 1.50 the edge survives normal variance. The figure is fragile on small samples, so read it beside the trade count.',
          stats: [
            { label: 'Gross profit', value: '+$' + num(grossWin), col: 'var(--pos)' },
            { label: 'Gross loss', value: '−$' + num(grossLoss), col: 'var(--neg)' },
            { label: 'Net', value: '+$' + num(D.pnl), col: 'var(--pos)' },
            { label: 'Sample', value: num(D.closed) + ' trades' },
            { label: 'All-time', value: B.pf.toFixed(2), col: 'var(--text-mid)' },
            { label: 'Scale top', value: pfCap.toFixed(2), col: 'var(--text-mid)' },
          ],
          trendTitle: 'Profit factor by sub-period',
          trend: trendOf(isFinite(pf) ? pf : pfCap, 0.24, 6151, (v) => v.toFixed(2), 'Profit factor'),
          notes: [
            {
              icon: 'ph-target',
              col: 'var(--pos)',
              text:
                'Each 0.10 of profit factor here is worth roughly $' +
                num(Math.round(grossLoss * 0.1)) +
                ' on the same gross loss.',
            },
            {
              icon: 'ph-scissors',
              col: 'var(--amb-2)',
              text:
                'Cutting the worst 10% of losers would lift it to about ' +
                (isFinite(pf) ? (grossWin / (grossLoss * 0.9)).toFixed(2) : '∞') +
                '.',
            },
            {
              icon: 'ph-clock',
              col: 'var(--muted2)',
              text:
                worstSess.name +
                ' contributes the largest share of the loss side — the session view has the split.',
            },
          ],
          ctaLabel: 'Inspect the losing side',
          ctaHint: 'Journal, filtered to losing trades ' + D.label,
          go: jGo,
        },
      ),
      wire(
        miniCards[2]!,
        {
          head: 'Expectancy · ' + D.label,
          value: (expectancy >= 0 ? '+$' : '−$') + num(Math.abs(expectancy)),
          valueCol: expectancy >= 0 ? 'var(--pos)' : 'var(--neg)',
          rows: [
            { k: 'Per trade', v: 'over ' + num(D.closed) + ' trades' },
            { k: 'All-time', v: '+$' + num(B.expectancy) },
          ],
          note: 'Click for the full breakdown',
        },
        {
          kicker: 'Metric · ' + D.label,
          title: 'Expectancy',
          value: (expectancy >= 0 ? '+$' : '−$') + num(Math.abs(expectancy)),
          delta: dAbs(expectancy, B.expectancy, 0, '$').txt,
          deltaCol: dAbs(expectancy, B.expectancy, 0, '$').col,
          sub: 'average outcome of one more trade at your current edge',
          body: 'Net P&L ÷ closed trades. It folds hit rate and payoff into one number: what one more trade is worth if nothing changes. Multiply it by your planned trade count to sanity-check a monthly target.',
          stats: [
            { label: 'Net P&L', value: '+$' + num(D.pnl), col: 'var(--pos)' },
            { label: 'Closed', value: num(D.closed) },
            { label: 'Avg win', value: '$' + num(avgWin), col: 'var(--pos)' },
            { label: 'Avg loss', value: '$' + num(avgLoss), col: 'var(--neg)' },
            { label: 'All-time', value: '+$' + num(B.expectancy), col: 'var(--text-mid)' },
            { label: '100 trades', value: '+$' + num(expectancy * 100), col: 'var(--text-mid)' },
          ],
          trendTitle: 'Expectancy by sub-period',
          trend: trendOf(
            expectancy,
            0.35,
            8291,
            (v) => (v >= 0 ? '+$' : '−$') + num(Math.round(Math.abs(v))),
            'Expectancy',
          ),
          notes: [
            {
              icon: 'ph-equals',
              col: 'var(--muted2)',
              text:
                'Decomposes to ' +
                (tradePct / 100).toFixed(2) +
                ' × $' +
                num(avgWin) +
                ' − ' +
                (1 - tradePct / 100).toFixed(2) +
                ' × $' +
                num(avgLoss) +
                '.',
            },
            {
              icon: 'ph-arrow-up-right',
              col: 'var(--pos)',
              text:
                'Holding winners to ' +
                (isFinite(ratio) ? (ratio + 0.25).toFixed(2) : '∞') +
                'R at the same hit rate would move it to about +$' +
                num(Math.round(avgLoss * ((tradePct / 100) * (ratio + 0.25) - (1 - tradePct / 100)))) +
                '.',
            },
            {
              icon: 'ph-warning-circle',
              col: 'var(--amb-2)',
              text: 'A single outsized loss moves this figure more than ten average trades do — check the risk cap in Prepare.',
            },
          ],
          ctaLabel: 'Open the trade log in the journal',
          ctaHint: 'Journal, all closed trades ' + D.label,
          go: jGo,
        },
      ),
      wire(
        miniCards[3]!,
        {
          head: 'Avg win / loss · ' + D.label,
          value: '$' + num(avgWin) + ' / $' + num(avgLoss),
          valueCol: 'var(--text)',
          rows: [
            {
              k: 'Payoff',
              v: (isFinite(ratio) ? ratio.toFixed(2) : '∞') + 'R',
              col: ratio >= 1 ? 'var(--pos)' : 'var(--amb-2)',
            },
            { k: 'All-time', v: B.ratio.toFixed(2) + 'R' },
          ],
          note: 'Click for the full breakdown',
        },
        {
          kicker: 'Metric · ' + D.label,
          title: 'Average win / loss',
          value: '$' + num(avgWin) + ' / $' + num(avgLoss),
          delta: dAbs(ratio, B.ratio, 2).txt + ' payoff',
          deltaCol: dAbs(ratio, B.ratio, 2).col,
          sub: 'payoff ' + (isFinite(ratio) ? ratio.toFixed(2) : '∞') + 'R · 1.00R is even money',
          body: 'The average winning trade against the average losing trade. Divided, it is your payoff ratio — the number that sets how often you actually need to be right.',
          stats: [
            { label: 'Avg win', value: '$' + num(avgWin), col: 'var(--pos)' },
            { label: 'Avg loss', value: '$' + num(avgLoss), col: 'var(--neg)' },
            { label: 'Payoff', value: (isFinite(ratio) ? ratio.toFixed(2) : '∞') + 'R' },
            { label: 'Breakeven rate', value: breakeven.toFixed(1) + '%', col: 'var(--text-mid)' },
            {
              label: 'Actual rate',
              value: tradePct.toFixed(1) + '%',
              col: tradePct >= breakeven ? 'var(--pos)' : 'var(--neg)',
            },
            { label: 'All-time', value: B.ratio.toFixed(2) + 'R', col: 'var(--text-mid)' },
          ],
          trendTitle: 'Payoff by sub-period',
          trend: trendOf(isFinite(ratio) ? ratio : rCap, 0.22, 9377, (v) => v.toFixed(2) + 'R', 'Payoff'),
          notes: [
            {
              icon: 'ph-scales',
              col: 'var(--muted2)',
              text:
                'At ' +
                (isFinite(ratio) ? ratio.toFixed(2) : '∞') +
                'R you only need ' +
                breakeven.toFixed(1) +
                '% of trades to work — you are running ' +
                tradePct.toFixed(1) +
                '%.',
            },
            {
              icon: 'ph-hand-grabbing',
              col: 'var(--amb-2)',
              text: 'The gap between avg win and avg loss is where partial exits and trailing stops show up — both rules are in Prepare.',
            },
            {
              icon: 'ph-chart-line-up',
              col: 'var(--pos)',
              text: 'Payoff has more headroom than hit rate for most traders: it is a management change, not a selection change.',
            },
          ],
          ctaLabel: 'Compare winners and losers',
          ctaHint: 'Journal, sorted by result ' + D.label,
          go: jGo,
        },
      ),
    ];

    const gaugesWired = [
      wireFigs(
        wire(
          gaugeCards[0]!,
          {
            head: 'Trade Win % · ' + D.label,
            value: tradePct.toFixed(1) + '%',
            valueCol: 'var(--text)',
            rows: [
              { k: 'Winning trades', v: num(D.wins), col: 'var(--pos)' },
              { k: 'Closed trades', v: num(D.closed) },
              { k: 'Open (excluded)', v: String(D.open) },
            ],
            note: 'Click for the full breakdown',
          },
          {
            kicker: 'Performance · ' + D.label,
            title: 'Trade Win %',
            value: tradePct.toFixed(1) + '%',
            delta: dPts(tradePct, B.tradePct).txt,
            deltaCol: dPts(tradePct, B.tradePct).col,
            sub: num(D.wins) + ' of ' + num(D.closed) + ' closed trades finished green',
            body: 'Winning closed trades ÷ total closed trades × 100, on a fixed 0–100% dial. Open positions are held out until they close, so the figure never moves on unrealised P&L.',
            stats: [
              { label: 'Winning', value: num(D.wins), col: 'var(--pos)' },
              { label: 'Losing', value: num(losers), col: 'var(--neg)' },
              { label: 'Closed', value: num(D.closed) },
              { label: 'Open', value: String(D.open), col: 'var(--text-mid)' },
              { label: 'All-time', value: B.tradePct.toFixed(1) + '%', col: 'var(--text-mid)' },
              { label: 'Breakeven need', value: breakeven.toFixed(1) + '%', col: 'var(--text-mid)' },
            ],
            trendTitle: 'Trade Win % by sub-period',
            trend: trendOf(tradePct, 0.18, 3313, (v) => v.toFixed(1) + '%', 'Trade Win %'),
            notes: [
              {
                icon: 'ph-crosshair',
                col: 'var(--pos)',
                text:
                  bestSess.name +
                  ' at ' +
                  bestSess.wr +
                  '% is pulling the average up; ' +
                  worstSess.name +
                  ' at ' +
                  worstSess.wr +
                  '% is pulling it down.',
              },
              {
                icon: 'ph-list-numbers',
                col: 'var(--muted2)',
                text:
                  'Peak entry window is ' +
                  peakHour +
                  ':00 — concentration outside your plan is what Session Adherence penalises.',
              },
              {
                icon: 'ph-scales',
                col: 'var(--muted2)',
                text:
                  'This is the pillar input for Consistency; the score reads it after Bayesian shrinkage on ' +
                  num(D.closed) +
                  ' trades.',
              },
            ],
            ctaLabel: 'Review winners in the journal',
            ctaHint: 'Journal, filtered to winning trades ' + D.label,
            go: jGo,
          },
        ),
        [
          {
            head: 'Winning trades · ' + D.label,
            value: num(D.wins),
            valueCol: 'var(--pos)',
            rows: [{ k: 'Of closed', v: num(D.closed) }],
            note: 'Closed trades that finished in profit',
          },
          {
            head: 'Closed trades · ' + D.label,
            value: num(D.closed),
            valueCol: 'var(--text)',
            rows: [{ k: 'Open, excluded', v: String(D.open) }],
            note: 'All trades closed ' + D.label,
          },
        ],
      ),
      wireFigs(
        wire(
          gaugeCards[1]!,
          {
            head: 'Day Win % · ' + D.label,
            value: dayPct.toFixed(1) + '%',
            valueCol: 'var(--text)',
            rows: [
              { k: 'Profitable days', v: num(D.daysProfit), col: 'var(--pos)' },
              { k: 'Trading days', v: num(D.daysTraded) },
              { k: 'All-time', v: B.dayPct.toFixed(1) + '%' },
            ],
            note: 'Click for the full breakdown',
          },
          {
            kicker: 'Performance · ' + D.label,
            title: 'Day Win %',
            value: dayPct.toFixed(1) + '%',
            delta: dPts(dayPct, B.dayPct).txt,
            deltaCol: dPts(dayPct, B.dayPct).col,
            sub:
              num(D.daysProfit) +
              ' of ' +
              num(D.daysTraded) +
              (D.daysTraded === 1 ? ' trading day closed green' : ' trading days closed green'),
            body: 'Profitable trading days ÷ days with at least one closed trade. Flat days are excluded entirely, so sitting out never dilutes it. Day Win % above Trade Win % usually means winners are being held into the close.',
            stats: [
              { label: 'Green days', value: num(D.daysProfit), col: 'var(--pos)' },
              { label: 'Red days', value: num(D.daysTraded - D.daysProfit), col: 'var(--neg)' },
              { label: 'Trading days', value: num(D.daysTraded) },
              { label: 'All-time', value: B.dayPct.toFixed(1) + '%', col: 'var(--text-mid)' },
              {
                label: 'Avg day',
                value: '+$' + num(Math.round(D.pnl / Math.max(1, D.daysTraded))),
                col: 'var(--pos)',
              },
              {
                label: 'Trades / day',
                value: (D.closed / Math.max(1, D.daysTraded)).toFixed(1),
                col: 'var(--text-mid)',
              },
            ],
            trendTitle: 'Day Win % by sub-period',
            trend: trendOf(dayPct, 0.2, 5077, (v) => v.toFixed(1) + '%', 'Day Win %'),
            notes: [
              {
                icon: 'ph-calendar-check',
                col: 'var(--pos)',
                text:
                  'Day Win % sits ' +
                  (dayPct >= tradePct ? 'above' : 'below') +
                  ' Trade Win % — ' +
                  (dayPct >= tradePct
                    ? 'good days are being finished, not given back.'
                    : 'winning trades are being undone later in the session.'),
              },
              {
                icon: 'ph-moon',
                col: 'var(--muted2)',
                text: 'Flat days are hatched on the calendar and excluded here, so a quiet week cannot hurt the rate.',
              },
              {
                icon: 'ph-shield-check',
                col: 'var(--muted2)',
                text: 'Daily loss caps in Prepare are the fastest lever on this figure — one uncapped day flips a green week.',
              },
            ],
            ctaLabel: 'Open the losing days',
            ctaHint: 'Journal, filtered to red trading days ' + D.label,
            go: jGo,
          },
        ),
        [
          {
            head: 'Profitable days · ' + D.label,
            value: num(D.daysProfit),
            valueCol: 'var(--pos)',
            rows: [{ k: 'Of trading days', v: num(D.daysTraded) }],
            note: 'Days that finished net positive',
          },
          {
            head: 'Trading days · ' + D.label,
            value: num(D.daysTraded),
            valueCol: 'var(--text)',
            rows: [
              {
                k: 'Avg per day',
                v: '+$' + num(Math.round(D.pnl / Math.max(1, D.daysTraded))),
                col: 'var(--pos)',
              },
            ],
            note: 'Days with at least one closed trade',
          },
        ],
      ),
      wireFigs(
        wire(
          gaugeCards[2]!,
          {
            head: 'ROI · ' + D.label,
            value: '+' + D.roi.toFixed(2) + '%',
            valueCol: 'var(--pos)',
            rows: [
              { k: 'Net P&L', v: '+$' + num(D.pnl), col: 'var(--pos)' },
              { k: 'Deposit', v: '$500,000' },
              { k: 'Scale top', v: '+' + D.scale + '%' },
            ],
            note: 'Click for the full breakdown',
          },
          {
            kicker: 'Performance · ' + D.label,
            title: 'Return on investment',
            value: '+' + D.roi.toFixed(2) + '%',
            delta: '+$' + num(D.pnl) + ' realised',
            deltaCol: 'var(--pos)',
            sub: 'on $500,000 deposited across all accounts',
            body: 'Realised net P&L ÷ deposited capital × 100. The dial scale is derived from this timeframe rather than fixed, so the needle position is comparable within the period but not across periods. Open positions are excluded.',
            stats: [
              { label: 'Net P&L', value: '+$' + num(D.pnl), col: 'var(--pos)' },
              { label: 'Deposit', value: '$500,000' },
              { label: 'Scale top', value: '+' + D.scale + '%', col: 'var(--text-mid)' },
              { label: 'Dial fill', value: Math.round(roiFill) + '%', col: 'var(--text-mid)' },
              { label: 'Per trade', value: '+$' + num(expectancy), col: 'var(--pos)' },
              {
                label: 'Per day',
                value: '+$' + num(Math.round(D.pnl / Math.max(1, D.daysTraded))),
                col: 'var(--pos)',
              },
            ],
            trendTitle: 'ROI by sub-period',
            trend: trendOf(D.roi, 0.4, 7639, (v) => '+' + v.toFixed(2) + '%', 'ROI'),
            // The design's own words about the ROI dial, carried verbatim. Copy that annotates a figure belongs
            // beside it: the sim dataset holds inputs, and a sentence is not one.
            // data-lint-allow: three static notes, design copy rather than data
            notes: [
              {
                icon: 'ph-coins',
                col: 'var(--pos)',
                text: 'Every 1% here is $5,000 on the current deposit base.',
              },
              {
                icon: 'ph-chart-donut',
                col: 'var(--muted2)',
                text: 'Return is not risk — the score reads it against drawdown before it credits any points.',
              },
              {
                icon: 'ph-buildings',
                col: 'var(--muted2)',
                text: 'Prop payouts and personal capital are pooled here; Compare Accounts separates them.',
              },
            ],
            ctaLabel: 'Break the return down in Analytics',
            ctaIcon: 'ph-chart-bar',
            ctaHint: 'Analytics, same timeframe',
            go: anGo,
          },
        ),
        [
          {
            head: 'Net P&L · ' + D.label,
            value: '+$' + num(D.pnl),
            valueCol: 'var(--pos)',
            rows: [{ k: 'Per trade', v: '+$' + num(expectancy), col: 'var(--pos)' }],
            note: 'Realised only — open positions excluded',
          },
          {
            head: 'Deposit',
            value: '$500,000',
            valueCol: 'var(--text)',
            rows: [{ k: 'Accounts', v: 'all' }],
            note: 'Capital deposited across all accounts',
          },
        ],
      ),
    ];

    /** A card's own bars, each hoverable and each opening its own breakdown. Separate from `wireFigs` because a
     *  bar is a figure in its own right — clicking the session chart should open *that session*, not the card. */
    const wireBars = <T extends { bars: readonly object[] }>(
      card: T,
      spec: (bar: T['bars'][number], i: number) => { hover: TipSpec; drill: DrillSpec },
    ): Omit<T, 'bars'> & { bars: (T['bars'][number] & HoverPack & { click: () => void })[] } => ({
      ...card,
      bars: card.bars.map((b, i) => {
        const { hover, drill } = spec(b, i);
        return { ...b, ...hoverPack(c, hover), click: () => openDrill(c, drill) };
      }),
    });

    const pfMetrics = [
      wireBars(
        wireFigs(
          wire(
            pfMetricCards[0]!,
            {
              head: 'Session performance · ' + D.label,
              value: bestSess.name + ' leads',
              valueCol: 'var(--text)',
              rows: sessPnl.map((sp) => ({
                k: sp.name,
                v: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
                col: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
              })),
              note: 'Click a bar for that session',
            },
            {
              kicker: 'Breakdown · ' + D.label,
              title: 'Session performance',
              value: bestSess.name,
              delta: (bestSess.pnl >= 0 ? '+$' : '−$') + num(Math.abs(bestSess.pnl)),
              deltaCol: bestSess.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
              sub: 'realised P&L split on your Prepare session windows',
              body: 'Trades are assigned to a session by entry time in broker hours. Sessions overlap, so a London entry held into New York still counts as London — the window you chose is what is being scored.',
              stats: sessPnl.map((sp) => ({
                label: sp.name,
                value: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
                col: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
              })),
              rowsTitle: 'Every session',
              rowsIcon: 'ph-globe-hemisphere-west',
              rowsMeta: sessPnl.length + ' windows',
              rows: sessPnl
                .slice()
                .sort((a, b) => b.pnl - a.pnl)
                .map((sp) => ({
                  lead: Math.round((Math.abs(sp.pnl) / sessAbs) * 100) + '%',
                  name: sp.name,
                  sub: sp.wr + '% win rate · ' + (sp.pnl >= 0 ? 'net positive' : 'net negative'),
                  value: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
                  col: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
                  meta: '',
                })),
              notes: [
                {
                  icon: 'ph-star',
                  col: 'var(--pos)',
                  text:
                    bestSess.name +
                    ' produced ' +
                    Math.round((Math.abs(bestSess.pnl) / sessAbs) * 100) +
                    '% of the gross movement at a ' +
                    bestSess.wr +
                    '% hit rate.',
                },
                {
                  icon: 'ph-prohibit',
                  col: 'var(--neg)',
                  text:
                    'Dropping ' +
                    worstSess.name +
                    ' entirely would change net P&L by ' +
                    (-worstSess.pnl >= 0 ? '+$' : '−$') +
                    num(Math.abs(worstSess.pnl)) +
                    '.',
                },
                {
                  icon: 'ph-alarm',
                  col: 'var(--muted2)',
                  text: 'Session Adherence in the score compares this split against the windows you armed, not against the best one.',
                },
              ],
              ctaLabel: 'Filter the journal by session',
              ctaHint: 'Journal, grouped by session ' + D.label,
              go: jGo,
            },
          ),
          [
            {
              head: 'Best session · ' + D.label,
              value: bestSess.name,
              valueCol: 'var(--pos)',
              rows: [
                {
                  k: 'Net P&L',
                  v: (bestSess.pnl >= 0 ? '+$' : '−$') + num(Math.abs(bestSess.pnl)),
                  col: bestSess.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
                },
                { k: 'Win rate', v: bestSess.wr + '%' },
              ],
              note: '',
            },
            {
              head: 'Weakest session · ' + D.label,
              value: worstSess.name,
              valueCol: 'var(--neg)',
              rows: [
                {
                  k: 'Net P&L',
                  v: (worstSess.pnl >= 0 ? '+$' : '−$') + num(Math.abs(worstSess.pnl)),
                  col: worstSess.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
                },
                { k: 'Win rate', v: worstSess.wr + '%' },
              ],
              note: '',
            },
          ],
        ),
        (_bar, i) => {
          const sp = sessPnl[i]!;
          const shareP = Math.round((Math.abs(sp.pnl) / sessAbs) * 100);
          return {
            hover: {
              head: sp.name + ' session · ' + D.label,
              value: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
              valueCol: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
              rows: [
                { k: 'Win rate', v: sp.wr + '%' },
                { k: 'Share of gross', v: shareP + '%' },
              ],
              note: 'Click for the session breakdown',
            },
            drill: {
              kicker: 'Session · ' + D.label,
              title: sp.name,
              value: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
              valueCol: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
              delta: sp.wr + '% win rate',
              deltaCol: sp.wr >= 50 ? 'var(--pos)' : 'var(--amb-2)',
              sub: shareP + '% of gross movement ' + D.label,
              body:
                'Trades entered inside the ' +
                sp.name +
                ' window, assigned on entry time in broker hours. A position held past the close still belongs to the session it was opened in — that is the window your plan committed to.',
              stats: [
                {
                  label: 'Net P&L',
                  value: (sp.pnl >= 0 ? '+$' : '−$') + num(Math.abs(sp.pnl)),
                  col: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
                },
                { label: 'Win rate', value: sp.wr + '%' },
                { label: 'Share of gross', value: shareP + '%', col: 'var(--text-mid)' },
                {
                  label: 'vs best',
                  value: sp.name === bestSess.name ? 'leading' : '−$' + num(Math.abs(bestSess.pnl - sp.pnl)),
                  col: sp.name === bestSess.name ? 'var(--pos)' : 'var(--muted2)',
                },
              ],
              notes: [
                {
                  icon: sp.pnl >= 0 ? 'ph-trend-up' : 'ph-trend-down',
                  col: sp.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
                  text:
                    sp.name +
                    ' is ' +
                    (sp.pnl >= 0 ? 'contributing' : 'costing') +
                    ' ' +
                    (sp.pnl >= 0 ? '+$' : '−$') +
                    num(Math.abs(sp.pnl)) +
                    ' ' +
                    D.label +
                    '.',
                },
                {
                  icon: 'ph-percent',
                  col: 'var(--muted2)',
                  text:
                    'Its ' +
                    sp.wr +
                    '% hit rate sits ' +
                    (sp.wr >= tradePct ? 'above' : 'below') +
                    ' your blended ' +
                    tradePct.toFixed(1) +
                    '%.',
                },
                {
                  icon: 'ph-alarm',
                  col: 'var(--muted2)',
                  text: 'Session Adherence scores whether you traded this window on purpose, not whether it paid.',
                },
              ],
              ctaLabel: 'Open ' + sp.name + ' trades in the journal',
              ctaHint: 'Journal, filtered to ' + sp.name + ' ' + D.label,
              go: jGo,
            },
          };
        },
      ),
      wireBars(
        wireFigs(
          wire(
            pfMetricCards[1]!,
            {
              head: 'Trade distribution by hour · ' + D.label,
              value: tied ? 'No single peak' : peakHour + ':00',
              valueCol: 'var(--text)',
              rows: [
                { k: 'Closed trades', v: num(D.closed) },
                { k: 'Bucket width', v: blockSize + (blockSize === 1 ? ' hour' : ' hours') },
                { k: 'Median hour', v: medianHour + ':00' },
              ],
              note: 'Click a bar for that window',
            },
            {
              kicker: 'Breakdown · ' + D.label,
              title: 'Trade distribution by hour',
              value: tied ? 'Level' : peakHour + ':00',
              delta: tied ? 'no single peak' : plural(hourPeak) + ' at peak',
              deltaCol: 'var(--text-mid)',
              sub: num(D.closed) + ' closed trades bucketed by entry hour, broker time',
              body:
                'Entry hour, not exit hour. ' +
                (blockSize > 1
                  ? 'Buckets are ' +
                    blockSize +
                    ' hours wide here because the sample is thin — narrow buckets on a small sample produce ties that read as signal when they are not. '
                  : '') +
                'Clustering outside your planned windows is exactly what Session Adherence deducts for.',
              stats: [
                { label: 'Closed', value: num(D.closed) },
                { label: tied ? 'Busiest' : 'Peak', value: tied ? 'Tied' : peakHour + ':00' },
                { label: 'At peak', value: plural(hourPeak), col: 'var(--text-mid)' },
                { label: 'Median hour', value: medianHour + ':00', col: 'var(--text-mid)' },
                { label: 'Buckets', value: String(buckets.length), col: 'var(--text-mid)' },
                { label: 'Bucket width', value: blockSize + 'h', col: 'var(--text-mid)' },
              ],
              rowsTitle: 'Every window',
              rowsIcon: 'ph-clock',
              rowsMeta: buckets.length + ' buckets',
              rows: buckets.map((b, i) => ({
                lead: b.label,
                name: plural(hourTrades[i]!),
                sub: Math.round((hourTrades[i]! / Math.max(1, hourTotal)) * 100) + '% of volume',
                value: hourTrades[i] === hourPeak ? 'Peak' : '',
                col: hourTrades[i] === hourPeak ? 'var(--acc)' : 'var(--muted2)',
                meta: '',
              })),
              notes: [
                {
                  icon: 'ph-clock-afternoon',
                  col: 'var(--muted2)',
                  text: 'Half your volume is entered before ' + medianHour + ':00.',
                },
                {
                  icon: tied ? 'ph-equals' : 'ph-arrow-fat-up',
                  col: tied ? 'var(--muted2)' : 'var(--acc)',
                  text: tied
                    ? 'Several buckets tie at ' +
                      plural(hourPeak) +
                      ' — there is no defensible peak in this sample.'
                    : peakHour +
                      ':00 carries ' +
                      Math.round((hourPeak / Math.max(1, hourTotal)) * 100) +
                      '% of all entries.',
                },
                {
                  icon: 'ph-target',
                  col: 'var(--muted2)',
                  text: 'Compare this against the windows armed in Prepare before changing anything.',
                },
              ],
              ctaLabel: 'See these entries in the journal',
              ctaHint: 'Journal, sorted by entry time ' + D.label,
              go: jGo,
            },
          ),
          [
            {
              head: tied ? 'Busiest window' : 'Peak hour',
              value: tied ? 'Tied' : peakHour + ':00',
              valueCol: 'var(--text)',
              rows: [{ k: 'Trades', v: plural(hourPeak) }],
              note: '',
            },
            {
              head: 'Median hour',
              value: medianHour + ':00',
              valueCol: 'var(--text-mid)',
              rows: [{ k: 'Of volume', v: '50% before' }],
              note: '',
            },
          ],
        ),
        (_bar, i) => {
          const bk = buckets[i]!;
          const cnt = hourTrades[i]!;
          const sharePct = Math.round((cnt / Math.max(1, hourTotal)) * 100);
          return {
            hover: {
              head: 'Entries ' + bk.label + ':00',
              value: plural(cnt),
              valueCol: 'var(--text)',
              rows: [
                { k: 'Share of volume', v: sharePct + '%' },
                {
                  k: cnt === hourPeak ? 'Rank' : 'vs peak',
                  v: cnt === hourPeak ? 'busiest window' : '−' + plural(hourPeak - cnt),
                },
              ],
              note: 'Click for the window breakdown',
            },
            drill: {
              kicker: 'Entry window · ' + D.label,
              title: bk.label + ':00' + (blockSize > 1 ? ' block' : ''),
              value: plural(cnt),
              valueCol: 'var(--text)',
              delta: sharePct + '% of volume',
              deltaCol: 'var(--text-mid)',
              sub: blockSize > 1 ? blockSize + '-hour bucket, broker time' : 'single hour, broker time',
              body:
                'Trades entered in this window ' +
                D.label +
                '. Volume concentration matters more than the raw count: a window carrying most of your entries sets your effective strategy whether you planned it or not.',
              stats: [
                { label: 'Entries', value: String(cnt) },
                { label: 'Share', value: sharePct + '%', col: 'var(--text-mid)' },
                { label: 'Busiest', value: tied ? 'Tied' : peakHour + ':00', col: 'var(--text-mid)' },
                { label: 'Median', value: medianHour + ':00', col: 'var(--text-mid)' },
              ],
              notes: [
                {
                  icon: cnt === hourPeak ? 'ph-arrow-fat-up' : 'ph-minus',
                  col: cnt === hourPeak ? 'var(--acc)' : 'var(--muted2)',
                  text:
                    cnt === hourPeak
                      ? 'This is your heaviest window' + (tied ? ', tied with others' : '') + '.'
                      : 'It sits ' + plural(hourPeak - cnt) + ' below the busiest window.',
                },
                {
                  icon: 'ph-globe-hemisphere-west',
                  col: 'var(--muted2)',
                  text:
                    'Cross-check it against ' +
                    bestSess.name +
                    ', which is currently your strongest session.',
                },
                {
                  icon: 'ph-target',
                  col: 'var(--muted2)',
                  text: 'If this window is not in your armed plan, Session Adherence is already deducting for it.',
                },
              ],
              ctaLabel: 'See these entries in the journal',
              ctaHint: 'Journal, entries in ' + bk.label + ':00',
              go: jGo,
            },
          };
        },
      ),
    ];

    /* P&L calendar */
    const MN = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const CM_MIN = 0,
      CM_MAX = 6,
      CY = 2026; // Jan – Jul 2026
    const cm = Math.max(CM_MIN, Math.min(CM_MAX, S.calM));
    const cDays = new Date(CY, cm + 1, 0).getDate();
    const cFirst = (new Date(CY, cm, 1).getDay() + 6) % 7; // Monday-first
    const cToday = cm === CM_MAX ? 24 : 0; // partial current month
    const cash = (v: number) => (v < 0 ? '−$' : '+$') + Math.abs(v).toLocaleString('en-US');
    const entries: (CalEntry | null)[] = [];
    for (let i = 0; i < cFirst; i++) entries.push(null);
    // READ, NOT GENERATED. This loop used to derive each day's P&L from a pseudo-random generator seeded by the
    // calendar month — no ledger, identical figures for every trader, and a full month of invented wins and losses
    // shown to accounts that had never placed a trade. The generator now lives in the simulator's dataset, where it
    // reproduces the design reference for the sim binding; under the live binding this map is the `day` aggregate.
    // A day absent from the map is a day with no trading, which the calendar renders differently from a flat day.
    for (let d = 1; d <= cDays; d++) {
      const key = CY + '-' + String(cm + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      const v = DD.calendar[key] ?? null;
      entries.push({ label: String(d), val: v, cur: d === cToday, tipLabel: MN[cm]! + ' ' + d });
    }
    const POS = ['var(--pos)', 'var(--pos)', 'var(--pos)', 'var(--pos)', 'var(--pos)'] as const;
    const NEG = ['var(--neg)', 'var(--neg)', 'var(--neg)', 'var(--neg)', 'var(--neg)'] as const;
    // absolute P&L bands, so $100 and $2,000 always read at the same intensity month to month
    // data-lint-allow: absolute P&L intensity bands for the calendar ramp (presentation, §9.4)
    const BANDS = [250, 750, 1500, 3000] as const;
    const tier = (v: number): Band => {
      const m = Math.abs(v);
      return m >= BANDS[3] ? 4 : m >= BANDS[2] ? 3 : m >= BANDS[1] ? 2 : m >= BANDS[0] ? 1 : 0;
    };
    const TINT = ['7%', '9%', '11%', '13%', '15%'] as const;
    const EDGE = ['30%', '40%', '50%', '62%', '76%'] as const;
    /** The key the Trading-day drawer opens on: `YYYY-MM-DD` for the month on screen. */
    const dayKeyOf = (d: number): string =>
      CY + '-' + String(cm + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
    const cells = entries.map((e) => {
      if (!e)
        return {
          // Grid padding before the first of the month, not a day. It carries `pad` so the template can take it out
          // of the accessibility tree: the package renders every cell as a button, and a padding button with an
          // empty label is a control screen readers announce and nobody can use.
          pad: true,
          day: '',
          val: '',
          tip: '',
          bg: 'transparent',
          bd: 'transparent',
          img: 'none',
          fg: 'transparent',
          dayFg: 'transparent',
          dayFw: '500',
          dis: true,
          cur: 'default',
          ring: 'none',
          open: () => {},
        };
      const col = e.val === null ? '' : e.val > 0 ? POS[tier(e.val)] : NEG[tier(e.val)];
      const key = dayKeyOf(Number(e.label));
      return {
        day: e.label,
        val: e.val === null ? '' : cash(e.val),
        // A day with no trading opens nothing — there is no day to show, and a drawer of invented trades for a day
        // the trader did not trade is the fabrication ground rule 3 exists to prevent.
        dis: e.val === null,
        cur: e.val === null ? 'default' : 'pointer',
        ring: c.S.dayKey === key ? '0 0 0 2px var(--acc-fill)' : 'none',
        open: e.val === null ? () => {} : () => c.set({ dayKey: key, dayVal: e.val ?? 0, dayAI: 'idle' }),
        tip: e.tipLabel + ' · ' + (e.val === null ? 'no trading' : cash(e.val) + ' — open the trading day'),
        bg:
          e.val === null
            ? 'transparent'
            : 'color-mix(in srgb, ' + col + ' ' + TINT[tier(e.val)] + ', transparent)',
        bd: e.cur
          ? 'var(--text)'
          : e.val === null
            ? 'var(--l6)'
            : 'color-mix(in srgb, ' + col + ' ' + EDGE[tier(e.val)] + ', transparent)',
        img:
          e.val === null ? 'repeating-linear-gradient(45deg,var(--l10) 0 2px,transparent 2px 5px)' : 'none',
        fg: e.val === null ? 'var(--faint)' : col,
        dayFg: e.cur ? 'var(--text)' : 'var(--text-mid)',
        dayFw: e.cur ? '700' : '500',
        ...hoverPack(c, {
          head: e.tipLabel,
          value: e.val === null ? 'No trading' : cash(e.val),
          valueCol: e.val === null ? 'var(--muted)' : e.val > 0 ? 'var(--pos)' : 'var(--neg)',
          rows:
            e.val === null
              ? []
              : [
                  { k: 'Intensity band', v: 'tier ' + (tier(e.val) + 1) + ' of 5' },
                  {
                    k: 'Status',
                    v: e.val > 0 ? 'Winning day' : 'Losing day',
                    col: e.val > 0 ? 'var(--pos)' : 'var(--neg)',
                  },
                ],
          // The flat-day note is the honest one: a day with no positions closed is excluded from Day Win %, and a
          // reader comparing the calendar to that percentage needs to know why it does not count.
          note: e.val === null ? 'Flat day — excluded from Day Win %' : 'Click to open the trading day',
        }),
      };
    });

    /* multi-account equity curves */
    const ALL = DD.accounts;
    const count = Math.max(2, Math.min(ALL.length, c.props.accountCount ?? 5));
    const accts = ALL.slice(0, count);
    const DAY_L = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
    const WEEK_L = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const YEAR_L = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const ALL_L = ['Q3 24', 'Q4 24', 'Q1 25', 'Q2 25', 'Q3 25', 'Q4 25', 'Q1 26', 'Q2 26', 'Q3 26'];
    // `scale` is carried from the reference and no longer read by anything: it scaled the per-account random walk
    // that the ledger derivation below replaced. Kept rather than dropped so the table still matches the reference
    // line for line — the reference carries it dead too.
    const RANGES: Record<DashboardRangeId, McRange> = {
      day: { n: 10, sub: 'hourly · today', lbl: (i) => DAY_L[i]!, scale: 0.06 },
      week: { n: 7, sub: 'daily · this week', lbl: (i) => WEEK_L[i]!, scale: 0.2 },
      month: { n: 24, sub: 'daily · Jul 1 – Jul 24', lbl: (i) => String(i + 1), scale: 0.5 },
      year: { n: 12, sub: 'monthly · 2026', lbl: (i) => YEAR_L[i]!, scale: 1 },
      all: { n: 9, sub: 'quarterly · since Q3 24', lbl: (i) => ALL_L[i]!, scale: 2.4 },
    };
    const rk = isRange(S.mcRange) ? S.mcRange : 'month',
      R = RANGES[rk],
      n = R.n;
    const rand = (seed: number) => {
      let s = (seed * 9301 + 49297) % 233280;
      return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
      };
    };
    /* The global curve is the **ledger**, not a sum of guesses.
     *
     * It used to be the other way round: each account walked its own seeded path from its own starting balance and
     * the global curve was whatever they happened to add up to — which meant the equity chart and the Net P&L card
     * above it were two independent stories that agreed only by accident. Now the global curve is derived from the
     * account's real equity walked back by the range's own P&L series, and the accounts are *shares* of it: their
     * wobble is weighted to sum to zero and the last account absorbs the rounding, so the parts always reconcile
     * with the whole. The drawer says as much, and now it is true. */
    const NOW = DD.equity;
    const pnl = DD.eqSets[rk].map((pt) => pt[1]);
    while (pnl.length < n) pnl.push(0);
    const totalPnl = pnl.slice(0, n).reduce((a, b) => a + b, 0);
    const combined: number[] = [];
    {
      let v = NOW - totalPnl;
      for (let i = 0; i < n; i++) {
        v += pnl[i] ?? 0;
        combined[i] = Math.round(v);
      }
    }
    const wsum = accts.reduce((t, a) => t + a.start, 0);
    const w = accts.map((a) => a.start / wsum);
    const avgDrift = accts.reduce((t, a) => t + a.drift, 0) / accts.length;
    const rnds = accts.map((a) => rand(a.seed + n * 13));
    const walk = accts.map(() => 0);
    const series: number[][] = accts.map(() => []);
    for (let j = 0; j < n; j++) {
      const raw = accts.map((a, i) => {
        walk[i] =
          walk[i]! * 0.9 + (rnds[i]!() - 0.5) * ((a.vol * 1.8) / 100) + ((a.drift - avgDrift) / 100) * 0.4;
        return walk[i]!;
      });
      const mean = raw.reduce((t, r, i) => t + r * w[i]!, 0);
      let acc = 0;
      accts.forEach((_a, i) => {
        // The last account takes the remainder rather than its own rounded share: without it the accounts sum to
        // the ledger ±a few dollars, and "a few dollars" in a reconciliation is the whole problem.
        const val =
          i === accts.length - 1
            ? combined[j]! - acc
            : Math.round(w[i]! * combined[j]! * (1 + raw[i]! - mean));
        series[i]![j] = val;
        acc += val;
      });
    }
    const gl = S.mcAcct === 'global';
    const selIdx = gl
      ? -1
      : Math.max(
          0,
          accts.findIndex((a) => a.id === S.mcAcct),
        );
    const selAcct = accts[selIdx];
    const primary = {
      name: gl || !selAcct ? 'Global' : selAcct.name,
      col: gl || !selAcct ? 'var(--acc-deep)' : selAcct.col,
      data: gl || !selAcct ? combined : series[selIdx]!,
    };
    // axis follows the primary series; de-emphasised curves are shape-only, each mapped to its own extremes
    const lo0 = Math.min(...primary.data),
      hi0 = Math.max(...primary.data);
    const pad = (hi0 - lo0) * 0.14 || hi0 * 0.05;
    const lo = Math.max(0, lo0 - pad),
      hi = hi0 + pad;
    const X = (i: number) => (n < 2 ? 0 : (i / (n - 1)) * 1000);
    const Y = (v: number) => 292 - ((v - lo) / (hi - lo || 1)) * 284;
    const curve = (d: number[], yOf: (v: number) => number) => {
      let s = 'M' + X(0).toFixed(1) + ',' + yOf(d[0]!).toFixed(1);
      for (let i = 1; i < n; i++) {
        const cx = ((X(i - 1) + X(i)) / 2).toFixed(1);
        s +=
          ' C' +
          cx +
          ',' +
          yOf(d[i - 1]!).toFixed(1) +
          ' ' +
          cx +
          ',' +
          yOf(d[i]!).toFixed(1) +
          ' ' +
          X(i).toFixed(1) +
          ',' +
          yOf(d[i]!).toFixed(1);
      }
      return s;
    };
    const pathOf = (d: number[]) => curve(d, Y);
    const shapeOf = (d: number[]) => {
      // self-normalised into the plot band so every account stays legible
      const a = Math.min(...d),
        b = Math.max(...d),
        sp = b - a || 1;
      return curve(d, (v) => 268 - ((v - a) / sp) * 236);
    };
    const fmt = (v: number) =>
      (v < 0 ? '−$' : '$') +
      (Math.abs(v) >= 1e6 ? (Math.abs(v) / 1e6).toFixed(2) + 'M' : Math.round(Math.abs(v) / 1000) + 'k');
    const money = (v: number) => '$' + Math.round(v).toLocaleString('en-US');
    const pct = (a: number, b: number) => {
      const p = b ? ((a - b) / b) * 100 : 0;
      return (p >= 0 ? '+' : '−') + Math.abs(p).toFixed(2) + '%';
    };
    const chgCol = (a: number, b: number) => (a >= b ? 'var(--pos)' : 'var(--neg)');
    const step = n > 20 ? 3 : n > 12 ? 2 : 1;
    const h = S.mcHover;
    const on = h !== null && h >= 0 && h < n;
    const hh = h ?? 0; // index used only where `on` guards it
    const pd = primary.data;
    const rows = (
      gl ? [{ name: 'Global', col: 'var(--acc-deep)', data: combined, key: 'global' }] : []
    ).concat(accts.map((a, i) => ({ name: a.name, col: a.col, data: series[i]!, key: a.id })));
    return {
      ...pfView,
      pfMetrics,
      miniStats,
      gauges: gaugesWired.map((g, i) => ({
        ...g,
        bd: i ? '1px solid var(--l6)' : '1px solid transparent',
      })),
      gaugeRanges: RANGE_BTNS.map(([id, lbl]) => ({
        label: lbl,
        go: () => c.set({ gRange: id }),
        bg: gk === id ? 'var(--rail)' : 'transparent',
        fg: gk === id ? 'var(--rail-ink)' : 'var(--muted)',
        fw: gk === id ? '600' : '500',
      })),
      calPeriod: MN[cm]! + ' ' + CY,
      calMonthShort: MN[cm]!.slice(0, 3) + ' ' + CY,
      calDow: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      calCells: cells,
      calAtStart: cm <= CM_MIN,
      calAtEnd: cm >= CM_MAX,
      calPrevTitle: cm > CM_MIN ? MN[cm - 1]! + ' ' + CY : 'Earliest month',
      calNextTitle: cm < CM_MAX ? MN[cm + 1]! + ' ' + CY : 'Latest month',
      calPrevFg: cm > CM_MIN ? 'var(--text-mid)' : 'var(--ghost)',
      calNextFg: cm < CM_MAX ? 'var(--text-mid)' : 'var(--ghost)',
      calPrevCur: cm > CM_MIN ? 'pointer' : 'default',
      calNextCur: cm < CM_MAX ? 'pointer' : 'default',
      calPrev: () => c.set((st) => ({ calM: Math.max(CM_MIN, st.calM - 1) })),
      calNext: () => c.set((st) => ({ calM: Math.min(CM_MAX, st.calM + 1) })),
      mcValue: money(pd[n - 1]!),
      mcDelta:
        (pd[n - 1]! >= pd[0]! ? '+' : '−') +
        '$' +
        Math.abs(Math.round(pd[n - 1]! - pd[0]!)).toLocaleString('en-US') +
        '  ' +
        pct(pd[n - 1]!, pd[0]!),
      mcDeltaCol: chgCol(pd[n - 1]!, pd[0]!),
      mcPrimaryCol: primary.col,
      mcPrimaryD: pathOf(pd),
      mcAreaD: pathOf(pd) + ' L1000,300 L0,300 Z',
      mcCurves: accts
        .map((a, i) => ({ id: a.id, col: a.col, d: shapeOf(series[i]!) }))
        .filter((cv) => gl || cv.id !== accts[selIdx]!.id)
        .map((cv) => ({ d: cv.d, col: cv.col, w: '1.5', op: gl ? '0.45' : '0.22', fx: 'url(#mcblur)' })),
      // data-lint-allow: grid-line indices for the equity chart axis
      mcGrid: [0, 1, 2, 3].map((i) => ({ label: fmt(hi - ((hi - lo) * i) / 3) })),
      mcX: Array.from({ length: n }, (_, i) => ({
        label: i % step === 0 || i === n - 1 ? R.lbl(i) : '',
        fw: on && i === h ? '700' : '500',
        fg: on && i === h ? 'var(--text)' : 'var(--faint)',
      })),
      mcZones: Array.from({ length: n }, (_, i) => {
        const v = pd[i]!;
        const prev = i ? pd[i - 1]! : gl ? NOW - totalPnl : pd[0]!;
        const d = v - prev;
        const open0 = gl ? NOW - totalPnl : pd[0]!;
        const zLabel = rk === 'month' ? 'Jul ' + R.lbl(i) : R.lbl(i);
        return {
          on: () => c.set({ mcHover: i }),
          // The band is an invisible hit target over the chart, so its accessible name is the only thing that
          // tells a screen reader what point it is sitting on.
          aria: primary.name + ' at ' + zLabel + ', ' + money(v),
          click: () =>
            openDrill(c, {
              kicker: 'Equity curve · ' + primary.name,
              title: zLabel,
              value: money(v),
              valueCol: 'var(--text)',
              delta:
                (d >= 0 ? '+' : '−') +
                '$' +
                Math.abs(Math.round(d)).toLocaleString('en-US') +
                '  ' +
                pct(v, prev),
              deltaCol: chgCol(v, prev),
              sub: R.sub + ' · ' + (gl ? count + ' accounts combined' : 'isolated account'),
              body:
                'Equity at the close of ' +
                zLabel +
                '. The global curve is the ledger — its change across the range equals the Net P&L card for the same range — and every account curve is a share of that ledger rather than an independent series, so the parts always reconcile with the whole.',
              stats: [
                { label: 'Equity', value: money(v) },
                {
                  label: 'Step change',
                  value: (d >= 0 ? '+$' : '−$') + Math.abs(Math.round(d)).toLocaleString('en-US'),
                  col: chgCol(v, prev),
                },
                {
                  label: 'From open',
                  value:
                    (v - open0 >= 0 ? '+$' : '−$') + Math.abs(Math.round(v - open0)).toLocaleString('en-US'),
                  col: chgCol(v, open0),
                },
                { label: 'On the range', value: pct(v, open0), col: chgCol(v, open0) },
                { label: 'Series', value: gl ? 'Global' : primary.name, col: 'var(--text-mid)' },
                { label: 'Resolution', value: rk.toUpperCase(), col: 'var(--text-mid)' },
              ],
              rowsTitle: 'Accounts at ' + zLabel,
              rowsIcon: 'ph-wallet',
              rowsMeta: rows.length + ' series',
              rows: rows.map((r, j) => ({
                lead: r.key === 'global' ? 'ALL' : 'A' + j,
                name: r.name,
                sub: r.key === 'global' ? 'every account combined' : 'share of the ledger',
                value: money(r.data[i]!),
                col: 'var(--text)',
                meta: pct(r.data[i]!, i ? r.data[i - 1]! : r.data[0]!),
              })),
              notes: [
                {
                  icon: d >= 0 ? 'ph-trend-up' : 'ph-trend-down',
                  col: d >= 0 ? 'var(--pos)' : 'var(--neg)',
                  text:
                    'This step ' +
                    (d >= 0 ? 'added' : 'cost') +
                    ' ' +
                    (d >= 0 ? '+$' : '−$') +
                    Math.abs(Math.round(d)).toLocaleString('en-US') +
                    ' against the previous close.',
                },
                {
                  icon: 'ph-git-branch',
                  col: 'var(--muted2)',
                  text: 'De-emphasised curves are shape-only — each is normalised to its own extremes so a small account stays legible.',
                },
                {
                  icon: 'ph-scales',
                  col: 'var(--muted2)',
                  text: 'Drawdown, not equity, is what the score reads here: a steep climb with a deep retrace scores worse than a slow one.',
                },
              ],
              ctaLabel: 'Open this period in the journal',
              ctaIcon: 'ph-notebook',
              ctaHint: 'Journal, filtered to ' + zLabel,
              go: jGo,
            }),
        };
      }),
      mcLeave: () => c.set({ mcHover: null }),
      mcHoverOn: on,
      mcHoverX: on ? X(hh).toFixed(1) : '0',
      mcDotLeft: on
        ? 'calc(53px + ' +
          ((X(hh) / 1000) * 100).toFixed(2) +
          '% - ' +
          ((X(hh) / 1000) * 53).toFixed(1) +
          'px)'
        : '0px',
      mcDotTop: on ? ((Y(pd[hh]!) / 300) * 100).toFixed(2) + '%' : '0%',
      mcTipLeft: on
        ? 'calc(53px + (100% - 53px) * ' +
          Math.min(0.86, Math.max(0.14, n < 2 ? 0.5 : hh / (n - 1))).toFixed(3) +
          ')'
        : '50%',
      mcTipPeriod: R.lbl(on ? hh : 0) + ' · ' + rk.toUpperCase(),
      mcTipRows: on
        ? rows.map((r) => ({
            name: r.name,
            col: r.col,
            val: money(r.data[hh]!),
            chg: hh === 0 ? '—' : pct(r.data[hh]!, r.data[hh - 1]!),
            chgCol: hh === 0 ? 'var(--muted2)' : chgCol(r.data[hh]!, r.data[hh - 1]!),
            fw: r.name === primary.name ? '700' : '500',
            op: r.name === primary.name ? '1' : '0.62',
          }))
        : [],
      mcRanges: RANGE_BTNS.map(([id, lbl]) => ({
        label: lbl,
        go: () => c.set({ mcRange: id, mcHover: null }),
        bg: rk === id ? 'var(--rail)' : 'transparent',
        fg: rk === id ? 'var(--rail-ink)' : 'var(--muted)',
        fw: rk === id ? '600' : '500',
      })),
      mcAccts: [
        {
          id: 'global',
          name: 'Global',
          col: 'var(--acc-deep)',
          data: combined,
          title: 'Combined equity of all accounts',
        },
      ]
        .concat(
          accts.map((a, i) => ({
            id: a.id,
            name: a.name,
            col: a.col,
            data: series[i]!,
            title: a.name + ' · isolated equity curve',
          })),
        )
        .map((a) => {
          const sel = S.mcAcct === a.id;
          return {
            name: a.name,
            title: a.title,
            go: () => c.set({ mcAcct: a.id, mcHover: null }),
            dot: a.col,
            val: fmt(a.data[n - 1]!),
            bg: sel ? 'var(--l6)' : 'transparent',
            hover: sel ? 'var(--l6)' : 'var(--l5)',
            dotOp: '1',
            fg: sel ? 'var(--text)' : 'var(--muted)',
            op: sel ? '1' : '0.55',
            subFg: sel ? 'var(--text2)' : 'var(--faint)',
            fw: sel ? '700' : '500',
          };
        }),
    };
  })();

  return { ...eq, ...rest } satisfies Partial<ViewModel>;
}
export type DashboardModel = ReturnType<typeof buildDashboard>;
